import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Users, UserPlus, Settings, DollarSign, Clock, Briefcase, Crown, TrendingUp, CalendarIcon, FileText, Send, Sparkles, MessageCircle } from "lucide-react";
import Sidebar from "../components/Sidebar";
import CalendarioAsistencia from "../components/CalendarioAsistencia";
import CalendarioVentas from "../components/CalendarioVentas";
import MonitoreoPanel from "../components/MonitoreoPanel";
import NotificationBell from "../components/NotificationBell";
import PushNotificationSetup from "../components/PushNotificationSetup";
import TeamChat from "../components/TeamChat";
import NewsletterPanel from "../components/NewsletterPanel";
import JGMProPanel from "../components/JGMProPanel";
import EmailAliasesPanel from "../components/EmailAliasesPanel";
import MetasPanel from "../components/MetasPanel";
import SolicitudesPanel from "../components/SolicitudesPanel";
import CustomCalculator from "../components/CustomCalculator";
import VentasMymPanel from "../components/VentasMymPanel";
import ModelCredentialsSection from "../components/ModelCredentialsSection";
import ChatterlyPanel from "../components/ChatterlyPanel";
import { BACKEND_URL } from "../config";

const API = `${BACKEND_URL}/api`;

const COLORS = {
  total: '#FDB022',
  subs: '#F43F5E',
  messages: '#22C55E',
  tips: '#3B82F6',
  posts: '#A855F7'
};

function Dashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chatterlyStats, setChatterlyStats] = useState(null); // {totals, per_creator}
  const [statsSource, setStatsSource] = useState("manual"); // 'manual' | 'chatterly'
  const [models, setModels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newModel, setNewModel] = useState({ name: "", platform: "JG Models" });
  const [openModelDialog, setOpenModelDialog] = useState(false);
  const [timeFilter, setTimeFilter] = useState("today");
  const [openCustomFilter, setOpenCustomFilter] = useState(false);
  const [customFilterType, setCustomFilterType] = useState("date"); // "date", "month", "range"
  const [topEmpleados, setTopEmpleados] = useState([]);
  
  // Helper para obtener fecha en zona horaria de Colombia (UTC-5)
  const getColombiaDate = (date = new Date()) => {
    const colombiaOffset = -5 * 60; // UTC-5 en minutos
    const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
    const colombiaTime = new Date(utcTime + (colombiaOffset * 60000));
    return colombiaTime.toISOString().split('T')[0];
  };
  
  const [customDateRange, setCustomDateRange] = useState({
    startDate: "",
    endDate: "",
    startMonth: "",
    endMonth: ""
  });
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [goals, setGoals] = useState([]);
  const [openGoalDialog, setOpenGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState({ 
    user_id: "", 
    goal_amount: 0, 
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1, // 1-12
    period: 1, // 1 o 2
    fecha_inicio: "",
    fecha_fin: "",
    period_name: ""
  });
  const [editingGoal, setEditingGoal] = useState(null);
  const [openEditGoalDialog, setOpenEditGoalDialog] = useState(false);
  const [comisiones, setComisiones] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [editingUser, setEditingUser] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "chatter" });
  
  const [openCalendarioVentas, setOpenCalendarioVentas] = useState(false);
  const [empleadoSeleccionadoVentas, setEmpleadoSeleccionadoVentas] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // Profile states
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
    phone: "",
    profile_picture: ""
  });
  const [uploadingPicture, setUploadingPicture] = useState(false);

  // CEO Reports states
  const [ceoReports, setCeoReports] = useState([]);
  const [openCeoReportDialog, setOpenCeoReportDialog] = useState(false);
  const [editingCeoReport, setEditingCeoReport] = useState(null);
  const [newCeoReport, setNewCeoReport] = useState({
    date: new Date().toISOString().slice(0, 10),
    subs_amount: 0,
    messages_amount: 0,
    tips_amount: 0,
    notes: ""
  });

  // Contabilidad states
  const [reportesQuincenales, setReportesQuincenales] = useState([]);
  const [quincenaInicio, setQuincenaInicio] = useState(new Date().toISOString().slice(0, 10));
  const [quincenaFin, setQuincenaFin] = useState(new Date(new Date().setDate(new Date().getDate() + 15)).toISOString().slice(0, 10));
  const [resumenQuincenal, setResumenQuincenal] = useState(null);
  const [historialPeriodos, setHistorialPeriodos] = useState([]);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState(null);
  const [openHistorialDialog, setOpenHistorialDialog] = useState(false);
  const [openHistorialContabilidadDialog, setOpenHistorialContabilidadDialog] = useState(false);

  // Model states
  const [editingModel, setEditingModel] = useState(null);
  const [openEditModelDialog, setOpenEditModelDialog] = useState(false);
  
  // Agenda states
  const [weekTasks, setWeekTasks] = useState([]);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", day: "lunes", priority: "normal", task_type: "fija", order: 0 });
  const [editingTask, setEditingTask] = useState(null);

  // Work sessions state
  const [activeSessions, setActiveSessions] = useState([]);

  // Model Applications state
  const [modelApplications, setModelApplications] = useState([]);
  const [applicationFilter, setApplicationFilter] = useState("pendiente");

  // Goals History states
  const [goalsHistory, setGoalsHistory] = useState([]);
  const [filteredGoalsHistory, setFilteredGoalsHistory] = useState([]);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedGoalReports, setSelectedGoalReports] = useState([]);
  const [openReportsDialog, setOpenReportsDialog] = useState(false);
  const [selectedGoalDetails, setSelectedGoalDetails] = useState(null);
  const [historyDateFilter, setHistoryDateFilter] = useState({
    start: '',
    end: ''
  });

  // Monthly statistics states
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [statsDateRange, setStatsDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10), // Inicio del año actual
    end: new Date().toISOString().slice(0, 10) // Hoy
  });
  const [totalPeriodStats, setTotalPeriodStats] = useState(null);
  const [topModels, setTopModels] = useState([]);
  const [netoQuincenas, setNetoQuincenas] = useState([]);

  // Payslips / Nómina states
  const [payslips, setPayslips] = useState([]);
  const [openPayslipDialog, setOpenPayslipDialog] = useState(false);
  const [editingPayslip, setEditingPayslip] = useState(null);
  const [newPayslip, setNewPayslip] = useState({
    user_id: "",
    periodo: "",
    fecha_inicio: "",
    fecha_fin: "",
    salario_base: 0,
    dias_quincena: 15,
    dias_trabajados: 0,
    horas_trabajadas: 0,
    bonificaciones: 0,
    deducciones: 0,
    notas: ""
  });
  const [calculatingPayslip, setCalculatingPayslip] = useState(false);
  const [payslipWorkDetail, setPayslipWorkDetail] = useState(null);

  // Migration states
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);

  // Live camera feeds states
  const [liveFeeds, setLiveFeeds] = useState([]);

  // CRM states
  const [crmUsers, setCrmUsers] = useState([]);
  const [crmStats, setCrmStats] = useState(null);
  const [crmSearchTerm, setCrmSearchTerm] = useState("");
  const [crmFilterGasto, setCrmFilterGasto] = useState("all");
  const [crmFilterModelo, setCrmFilterModelo] = useState("all");
  const [crmFilterCashback, setCrmFilterCashback] = useState(false);
  const [openCrmDialog, setOpenCrmDialog] = useState(false);
  const [editingCrmUser, setEditingCrmUser] = useState(null);
  const [newCrmUser, setNewCrmUser] = useState({
    username: "",
    modelo_id: "",
    origen: "",
    gustos: "",
    solicitudes: "",
    nivel_gasto: "medium",
    gasto_total: 0,
    notas: "",
    hizo_cashback: false,
    fecha_cashback: "",
    monto_cashback: 0,
    notas_cashback: ""
  });
  
  // CRM Chat states
  const [crmMessages, setCrmMessages] = useState([]);
  const [newCrmMessage, setNewCrmMessage] = useState("");
  const [crmMessageType, setCrmMessageType] = useState("note");
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Team Chat states
  const [teamChatOpen, setTeamChatOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  // Fetch chat unread count and check for new messages
  const lastMessageCountRef = React.useRef(0);
  const audioRef = React.useRef(null);
  const [notificationsEnabled, setNotificationsEnabled] = React.useState(true);

  // Initialize notification sound
  React.useEffect(() => {
    audioRef.current = new Audio(`${BACKEND_URL}/api/sounds/notification.mp3`);
    audioRef.current.volume = 0.4;
    audioRef.current.load();
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const fetchChatUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`, { withCredentials: true });
      const newTotal = response.data.total || 0;
      
      // Play sound if there are new messages and chat is not open
      if (newTotal > lastMessageCountRef.current && !teamChatOpen && notificationsEnabled) {
        // Play sound
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
        // Browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('💬 Nuevo mensaje', {
            body: `Tienes ${newTotal} mensaje(s) sin leer`,
            icon: '/favicon.ico',
            tag: 'team-chat'
          });
        }
      }
      
      lastMessageCountRef.current = newTotal;
      setChatUnreadCount(newTotal);
    } catch (error) {
      console.error("Error fetching chat unread count:", error);
    }
  };

  // Fetch live camera feeds
  const fetchLiveFeeds = async () => {
    try {
      const response = await axios.get(`${API}/monitoreo/live-feeds`, { withCredentials: true });
      setLiveFeeds(response.data || []);
    } catch (error) {
      console.error("Error fetching live feeds:", error);
    }
  };

  // Check migration status on load
  const checkMigrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/migration-status`, { withCredentials: true });
      setMigrationStatus(response.data);
    } catch (error) {
      console.error("Error checking migration status:", error);
    }
  };

  const runMigration = async () => {
    setIsMigrating(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-chatter-reports`, {}, { withCredentials: true });
      toast.success(`Migracion completada: ${response.data.reportes_actualizados} reportes actualizados`);
      setMigrationStatus({ ...migrationStatus, necesitan_migracion: 0, migracion_necesaria: false });
      fetchData(); // Refresh dashboard data
    } catch (error) {
      toast.error("Error en la migracion");
    }
    setIsMigrating(false);
  };

  // Limpiar reportes duplicados del CEO
  const [isCleaningDuplicates, setIsCleaningDuplicates] = useState(false);
  const [showCleanupButton, setShowCleanupButton] = useState(true);
  
  const cleanupDuplicates = async () => {
    if (!window.confirm("¿Estás seguro de limpiar los reportes duplicados? Esto corregirá los totales de facturación.")) {
      return;
    }
    setIsCleaningDuplicates(true);
    try {
      const response = await axios.post(`${API}/admin/cleanup-duplicate-reports`, {}, { withCredentials: true });
      toast.success(`Limpieza completada: ${response.data.reportes_eliminados} reportes duplicados eliminados`);
      setShowCleanupButton(false);
      fetchData(); // Refresh dashboard data
    } catch (error) {
      toast.error("Error en la limpieza");
    }
    setIsCleaningDuplicates(false);
  };

  useEffect(() => {
    fetchData();
    fetchActiveSessions();
    fetchMonthlyStats();
    fetchCeoReports();
    checkMigrationStatus();
    fetchChatUnreadCount();
    // Actualizar sesiones activas cada 10 segundos
    const interval = setInterval(fetchActiveSessions, 10000);
    // Actualizar contador de mensajes no leídos cada 30 segundos
    const chatInterval = setInterval(fetchChatUnreadCount, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(chatInterval);
    };
  }, [timeFilter]);

  // Auto-refresh dashboard stats every 30 seconds when on overview
  useEffect(() => {
    let interval;
    if (activeTab === "overview") {
      interval = setInterval(() => {
        fetchData();
      }, 30000); // Actualizar cada 30 segundos
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, timeFilter]);

  useEffect(() => {
    fetchMonthlyStats();
  }, [statsDateRange]);

  useEffect(() => {
    fetchTopModels();
    fetchNetoQuincenas();
  }, []);

  // Fetch live feeds when on monitoreo tab
  useEffect(() => {
    let interval;
    if (activeTab === "monitoreo") {
      fetchLiveFeeds();
      interval = setInterval(fetchLiveFeeds, 3000); // Actualizar cada 3 segundos
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab]);

  useEffect(() => {
    filterGoalsHistory();
  }, [historyDateFilter, goalsHistory]);

  const fetchActiveSessions = async () => {
    try {
      const response = await axios.get(`${API}/work-session/active`, { withCredentials: true });
      setActiveSessions(response.data);
    } catch (error) {
      console.error("Error fetching active sessions:", error);
    }
  };

  const fetchModelApplications = async (status = null) => {
    try {
      const url = status ? `${API}/model-applications?status=${status}` : `${API}/model-applications`;
      const response = await axios.get(url, { withCredentials: true });
      setModelApplications(response.data);
    } catch (error) {
      console.error("Error fetching model applications:", error);
      toast.error("Error al cargar aplicaciones");
    }
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await axios.put(`${API}/model-applications/${applicationId}?status=${newStatus}`, {}, { withCredentials: true });
      toast.success(`Aplicación ${newStatus === 'aprobada' ? 'aprobada' : 'rechazada'} exitosamente`);
      fetchModelApplications(applicationFilter === "all" ? null : applicationFilter);
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error("Error al actualizar aplicación");
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta aplicación?")) return;
    
    try {
      await axios.delete(`${API}/model-applications/${applicationId}`, { withCredentials: true });
      toast.success("Aplicación eliminada");
      fetchModelApplications(applicationFilter === "all" ? null : applicationFilter);
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error("Error al eliminar aplicación");
    }
  };

  const fetchData = async () => {
    try {
      const now = new Date();
      let startDate, endDate;
      
      if (timeFilter === "yesterday") {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = getColombiaDate(yesterday);
      } else if (timeFilter === "today") {
        startDate = endDate = getColombiaDate(now);
      } else if (timeFilter === "week") {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = getColombiaDate(weekAgo);
        endDate = getColombiaDate(now);
      } else if (timeFilter === "month") {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate = getColombiaDate(monthStart);
        endDate = getColombiaDate(now);
      } else if (timeFilter === "custom") {
        startDate = customDateRange.startDate;
        endDate = customDateRange.endDate || customDateRange.startDate;
      }

      const [statsRes, modelsRes, usersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats?start_date=${startDate}&end_date=${endDate}`, { withCredentials: true }),
        axios.get(`${API}/models`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);

      setStats(statsRes.data);
      setModels(modelsRes.data);
      setUsers(usersRes.data);

      // Fetch de stats Chatterly (mes actual) — usado por el toggle Manual/Chatterly.
      try {
        const ctRes = await axios.get(`${API}/chatterly/stats/monthly`, { withCredentials: true });
        setChatterlyStats(ctRes.data);
      } catch (e) {
        // Chatterly puede no estar configurado en algunos entornos — no bloquear
        setChatterlyStats(null);
      }
      
      // Archivar metas vencidas automáticamente
      try {
        await axios.post(`${API}/goals/archive-expired`, {}, { withCredentials: true });
      } catch (e) {
        console.log("No hay metas vencidas para archivar");
      }
      
      // Fetch goals (ahora solo metas activas)
      const goalsRes = await axios.get(`${API}/goals`, { withCredentials: true });
      setGoals(goalsRes.data);
      
      // Fetch historial de metas completadas
      fetchGoalsHistory();
      
      // Fetch comisiones
      const comisionesRes = await axios.get(`${API}/admin/comisiones`, { withCredentials: true });
      setComisiones(comisionesRes.data);
      
      // Fetch top empleados con el mismo rango de fechas
      fetchTopEmpleados(startDate, endDate);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        toast.error("Sesión expirada. Por favor inicia sesión nuevamente");
        await logout();
        navigate("/");
      } else {
        toast.error("Error al cargar datos: " + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModel = async () => {
    if (!newModel.name) {
      toast.error("El nombre del modelo es requerido");
      return;
    }

    try {
      await axios.post(`${API}/models`, newModel, { withCredentials: true });
      toast.success("Modelo creado exitosamente");
      setNewModel({ name: "", platform: "JG Models" });
      setOpenModelDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Error al crear modelo");
    }
  };

  const fetchTopEmpleados = async (startDate, endDate) => {
    try {
      const response = await axios.get(`${API}/dashboard/top-empleados`, {
        params: { start_date: startDate, end_date: endDate },
        withCredentials: true
      });
      setTopEmpleados(response.data);
    } catch (error) {
      console.error("Error fetching top empleados:", error);
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${role}`, {}, { withCredentials: true });
      toast.success("Rol actualizado");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar rol");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const fetchGoalsHistory = async () => {
    try {
      const response = await axios.get(`${API}/goals/history`, { withCredentials: true });
      setGoalsHistory(response.data);
      setFilteredGoalsHistory(response.data); // Inicialmente sin filtro
    } catch (error) {
      toast.error("Error al cargar historial de metas");
    }
  };

  const filterGoalsHistory = () => {
    if (!historyDateFilter.start && !historyDateFilter.end) {
      setFilteredGoalsHistory(goalsHistory);
      return;
    }

    const filtered = goalsHistory.filter(goal => {
      const goalStartDate = new Date(goal.fecha_inicio);
      const goalEndDate = new Date(goal.fecha_fin);
      
      let matchesStart = true;
      let matchesEnd = true;

      if (historyDateFilter.start) {
        const filterStart = new Date(historyDateFilter.start);
        matchesStart = goalEndDate >= filterStart;
      }

      if (historyDateFilter.end) {
        const filterEnd = new Date(historyDateFilter.end);
        matchesEnd = goalStartDate <= filterEnd;
      }

      return matchesStart && matchesEnd;
    });

    setFilteredGoalsHistory(filtered);
  };

  const handleViewGoalReports = async (goal) => {
    try {
      // Fetch reports for the period of this goal
      const response = await axios.get(
        `${API}/reports?user_id=${goal.user_id}&start_date=${goal.fecha_inicio}&end_date=${goal.fecha_fin}`,
        { withCredentials: true }
      );
      setSelectedGoalReports(response.data);
      setSelectedGoalDetails(goal);
      setOpenReportsDialog(true);
    } catch (error) {
      toast.error("Error al cargar reportes");
    }
  };

  const fetchMonthlyStats = async () => {
    try {
      const response = await axios.get(`${API}/contabilidad/estadisticas/mensuales`, {
        params: {
          start_date: statsDateRange.start,
          end_date: statsDateRange.end
        },
        withCredentials: true
      });
      setMonthlyStats(response.data);
      
      // Calcular totales del período
      const total = response.data.reduce((acc, month) => ({
        total_valor_neto: acc.total_valor_neto + month.total_valor_neto,
        total_valor_neto_modelos: acc.total_valor_neto_modelos + month.total_valor_neto_modelos,
        total_valor_neto_agencia: acc.total_valor_neto_agencia + month.total_valor_neto_agencia,
        cantidad_meses: acc.cantidad_meses + 1
      }), { total_valor_neto: 0, total_valor_neto_modelos: 0, total_valor_neto_agencia: 0, cantidad_meses: 0 });
      
      setTotalPeriodStats(total);
    } catch (error) {
      console.error("Error fetching monthly stats:", error);
    }
  };

  const fetchTopModels = async () => {
    try {
      const response = await axios.get(`${API}/contabilidad/top-models`, {
        params: {
          limit: 5
        },
        withCredentials: true
      });
      setTopModels(response.data);
    } catch (error) {
      console.error("Error fetching top models:", error);
    }
  };

  const fetchNetoQuincenas = async () => {
    try {
      const response = await axios.get(`${API}/contabilidad/neto-quincenas`, { withCredentials: true });
      setNetoQuincenas(response.data);
    } catch (error) {
      console.error("Error fetching neto quincenas:", error);
    }
  };

  // Payslips functions
  const fetchPayslips = async () => {
    try {
      const response = await axios.get(`${API}/payslips`, { withCredentials: true });
      setPayslips(response.data);
    } catch (error) {
      toast.error("Error al cargar colillas de pago");
    }
  };

  // Auto-calculate work data when user_id, fecha_inicio, and fecha_fin are set
  const calculatePayslipWorkData = async (userId, fechaInicio, fechaFin) => {
    if (!userId || !fechaInicio || !fechaFin) return;
    
    setCalculatingPayslip(true);
    setPayslipWorkDetail(null);
    
    try {
      const response = await axios.get(`${API}/payslips/calculate-work-data`, {
        params: {
          user_id: userId,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin
        },
        withCredentials: true
      });
      
      const data = response.data;
      setPayslipWorkDetail(data);
      
      // Auto-fill the form with calculated values
      setNewPayslip(prev => ({
        ...prev,
        dias_trabajados: data.dias_trabajados,
        horas_trabajadas: data.horas_trabajadas
      }));
      
      if (data.dias_trabajados > 0) {
        toast.success(`✅ Calculado: ${data.dias_trabajados} días, ${data.horas_trabajadas}h`);
      } else {
        toast.info("No se encontraron registros de trabajo en este período");
      }
    } catch (error) {
      console.error("Error calculating work data:", error);
      toast.error("Error al calcular datos de trabajo");
    } finally {
      setCalculatingPayslip(false);
    }
  };

  // Handler for payslip employee change
  const handlePayslipUserChange = (userId) => {
    setNewPayslip(prev => ({ ...prev, user_id: userId }));
    calculatePayslipWorkData(userId, newPayslip.fecha_inicio, newPayslip.fecha_fin);
  };

  // Handler for payslip date changes
  const handlePayslipDateChange = (field, value) => {
    const updated = { ...newPayslip, [field]: value };
    setNewPayslip(updated);
    
    if (field === 'fecha_inicio') {
      calculatePayslipWorkData(updated.user_id, value, updated.fecha_fin);
    } else if (field === 'fecha_fin') {
      calculatePayslipWorkData(updated.user_id, updated.fecha_inicio, value);
    }
  };

  const handleCreatePayslip = async () => {
    if (!newPayslip.user_id || !newPayslip.periodo || !newPayslip.fecha_inicio || !newPayslip.fecha_fin) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }

    try {
      await axios.post(`${API}/payslips`, newPayslip, { withCredentials: true });
      toast.success("Colilla de pago creada exitosamente");
      setOpenPayslipDialog(false);
      setNewPayslip({
        user_id: "",
        periodo: "",
        fecha_inicio: "",
        fecha_fin: "",
        salario_base: 0,
        dias_quincena: 15,
        dias_trabajados: 0,
        horas_trabajadas: 0,
        bonificaciones: 0,
        deducciones: 0,
        notas: ""
      });
      setPayslipWorkDetail(null);
      fetchPayslips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear colilla");
    }
  };

  // Profile functions
  const openProfileEditor = () => {
    setProfileData({
      name: user?.name || "",
      phone: user?.phone || "",
      profile_picture: user?.profile_picture || ""
    });
    setOpenProfileDialog(true);
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen no puede superar 2MB");
      return;
    }

    // Validar tipo
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imágenes");
      return;
    }

    setUploadingPicture(true);
    try {
      // Convertir a base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        setProfileData(prev => ({ ...prev, profile_picture: base64 }));
        setUploadingPicture(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Error al procesar la imagen");
      setUploadingPicture(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      // Si hay foto nueva (base64), primero subimos la foto
      if (profileData.profile_picture && profileData.profile_picture.startsWith('data:image')) {
        await axios.post(`${API}/users/profile/picture`, 
          { picture_data: profileData.profile_picture },
          { withCredentials: true }
        );
      }
      
      // Actualizar nombre y teléfono (sin enviar la foto aquí)
      const params = new URLSearchParams();
      if (profileData.name) params.append("name", profileData.name);
      if (profileData.phone !== undefined && profileData.phone !== null) params.append("phone", profileData.phone);

      if (params.toString()) {
        await axios.put(`${API}/users/profile/me?${params.toString()}`, {}, { withCredentials: true });
      }
      
      // Actualizar el usuario en el contexto local
      if (user) {
        user.name = profileData.name || user.name;
        user.phone = profileData.phone;
        if (profileData.profile_picture) {
          user.profile_picture = profileData.profile_picture;
        }
      }
      
      toast.success("Perfil actualizado correctamente");
      setOpenProfileDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar perfil");
    }
  };


  const handleDeletePayslip = async (payslipId) => {
    try {
      await axios.delete(`${API}/payslips/${payslipId}`, { withCredentials: true });
      toast.success("Colilla eliminada");
      fetchPayslips();
    } catch (error) {
      toast.error("Error al eliminar colilla");
    }
  };

  const handleTogglePayslipPaid = async (payslip) => {
    try {
      const newPaid = !payslip.pagado;
      await axios.put(
        `${API}/payslips/${payslip.id}`,
        { pagado: newPaid },
        { withCredentials: true }
      );
      toast.success(newPaid ? "Colilla marcada como pagada" : "Marcada como pendiente");
      fetchPayslips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar estado de pago");
    }
  };

  const handleEditPayslipObservaciones = async (payslip) => {
    const current = payslip.observaciones || "";
    const observaciones = window.prompt(
      `Observaciones para la colilla de ${payslip.user_name} (${payslip.periodo}):\n\nEjemplo: "Le quedaron debiendo $50.000 por bono mensual" o "Pago parcial, falta entregar el resto el viernes"`,
      current
    );
    if (observaciones === null) return; // canceló
    try {
      await axios.put(
        `${API}/payslips/${payslip.id}`,
        { observaciones },
        { withCredentials: true }
      );
      toast.success("Observación guardada");
      fetchPayslips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar observación");
    }
  };

  // CEO Reports functions
  const fetchCeoReports = async () => {
    try {
      // Cargamos en paralelo reportes del CEO y reportes de chatters.
      // El admin ve TODO en este tab.
      const [ceoRes, chatterRes] = await Promise.allSettled([
        axios.get(`${API}/reports/ceo`, { withCredentials: true }),
        axios.get(`${API}/reports`, { withCredentials: true }),
      ]);
      const ceo = ceoRes.status === "fulfilled" ? (ceoRes.value.data || []) : [];
      const chatters = chatterRes.status === "fulfilled" ? (chatterRes.value.data || []) : [];
      const ceoTagged = ceo.map((r) => ({ ...r, _source: "ceo" }));
      const chatterTagged = chatters.map((r) => ({ ...r, _source: "chatter" }));
      // Unimos y ordenamos por fecha desc, luego por created_at desc
      const merged = [...ceoTagged, ...chatterTagged].sort((a, b) => {
        const ad = a.date || "";
        const bd = b.date || "";
        if (ad !== bd) return bd.localeCompare(ad);
        const ac = a.created_at || "";
        const bc = b.created_at || "";
        return bc.localeCompare(ac);
      });
      setCeoReports(merged);
    } catch (error) {
      console.error("Error fetching reports (merged):", error);
    }
  };

  const handleCreateCeoReport = async () => {
    if (!newCeoReport.date) {
      toast.error("Selecciona una fecha");
      return;
    }

    try {
      await axios.post(`${API}/reports/ceo`, {
        date: newCeoReport.date,
        subs_amount: newCeoReport.subs_amount || 0,
        messages_amount: newCeoReport.messages_amount || 0,
        tips_amount: newCeoReport.tips_amount || 0,
        notes: newCeoReport.notes || ""
      }, { withCredentials: true });
      
      toast.success("Reporte guardado");
      setOpenCeoReportDialog(false);
      setNewCeoReport({
        date: new Date().toISOString().slice(0, 10),
        subs_amount: 0,
        messages_amount: 0,
        tips_amount: 0,
        notes: ""
      });
      await fetchCeoReports();
      await fetchData();
    } catch (error) {
      console.error("Error creating report:", error);
      // Si el reporte se creó pero hubo error en la respuesta, recargar de todos modos
      await fetchCeoReports();
      await fetchData();
      if (error.response?.status !== 200) {
        toast.error(error.response?.data?.detail || "Error al guardar reporte");
      }
    }
  };

  const handleEditCeoReport = (report) => {
    setEditingCeoReport({
      ...report,
      subs_amount: report.subs_amount || 0,
      messages_amount: report.messages_amount || 0,
      tips_amount: report.tips_amount || 0
    });
  };

  const handleUpdateCeoReport = async () => {
    if (!editingCeoReport) return;
    
    try {
      await axios.put(`${API}/reports/ceo/${editingCeoReport.id}`, {
        subs_amount: editingCeoReport.subs_amount || 0,
        messages_amount: editingCeoReport.messages_amount || 0,
        tips_amount: editingCeoReport.tips_amount || 0,
        notes: editingCeoReport.notes || ""
      }, { withCredentials: true });
      
      toast.success("Reporte actualizado");
      setEditingCeoReport(null);
      await fetchCeoReports();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar reporte");
    }
  };

  const handleDeleteCeoReport = async (reportId) => {
    try {
      await axios.delete(`${API}/reports/ceo/${reportId}`, { withCredentials: true });
      toast.success("Reporte eliminado");
      fetchCeoReports();
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar reporte");
    }
  };

  // CRM Functions
  const fetchCrmUsers = async () => {
    try {
      let url = `${API}/crm/users?`;
      if (crmSearchTerm) url += `search=${encodeURIComponent(crmSearchTerm)}&`;
      if (crmFilterGasto !== "all") url += `nivel_gasto=${crmFilterGasto}&`;
      if (crmFilterModelo !== "all") url += `modelo_id=${crmFilterModelo}&`;
      if (crmFilterCashback) url += `solo_cashback=true&`;
      
      const response = await axios.get(url, { withCredentials: true });
      setCrmUsers(response.data);
    } catch (error) {
      console.error("Error fetching CRM users:", error);
    }
  };

  const fetchCrmStats = async () => {
    try {
      const response = await axios.get(`${API}/crm/stats`, { withCredentials: true });
      setCrmStats(response.data);
    } catch (error) {
      console.error("Error fetching CRM stats:", error);
    }
  };

  const handleCreateCrmUser = async () => {
    if (!newCrmUser.username || !newCrmUser.modelo_id) {
      toast.error("Usuario y modelo son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/crm/users`, newCrmUser, { withCredentials: true });
      toast.success("Usuario CRM creado");
      setOpenCrmDialog(false);
      setNewCrmUser({
        username: "",
        modelo_id: "",
        origen: "",
        gustos: "",
        solicitudes: "",
        nivel_gasto: "medium",
        gasto_total: 0,
        notas: "",
        hizo_cashback: false,
        fecha_cashback: "",
        monto_cashback: 0,
        notas_cashback: ""
      });
      fetchCrmUsers();
      fetchCrmStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario CRM");
    }
  };

  const handleUpdateCrmUser = async () => {
    if (!editingCrmUser) return;
    try {
      await axios.put(`${API}/crm/users/${editingCrmUser.id}`, {
        username: editingCrmUser.username,
        modelo_id: editingCrmUser.modelo_id,
        origen: editingCrmUser.origen,
        gustos: editingCrmUser.gustos,
        solicitudes: editingCrmUser.solicitudes,
        nivel_gasto: editingCrmUser.nivel_gasto,
        gasto_total: editingCrmUser.gasto_total,
        notas: editingCrmUser.notas,
        hizo_cashback: editingCrmUser.hizo_cashback || false,
        fecha_cashback: editingCrmUser.fecha_cashback || null,
        monto_cashback: editingCrmUser.monto_cashback || 0,
        notas_cashback: editingCrmUser.notas_cashback || null,
      }, { withCredentials: true });
      toast.success("Usuario CRM actualizado");
      setEditingCrmUser(null);
      fetchCrmUsers();
      fetchCrmStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar usuario CRM");
    }
  };

  const handleDeleteCrmUser = async (userId) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    try {
      await axios.delete(`${API}/crm/users/${userId}`, { withCredentials: true });
      toast.success("Usuario CRM eliminado");
      fetchCrmUsers();
      fetchCrmStats();
    } catch (error) {
      toast.error("Error al eliminar usuario CRM");
    }
  };

  // CRM Chat Functions
  const fetchCrmMessages = async (crmUserId) => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/crm/users/${crmUserId}/messages`, { withCredentials: true });
      setCrmMessages(response.data || []);
    } catch (error) {
      console.error("Error fetching CRM messages:", error);
      setCrmMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendCrmMessage = async () => {
    if (!newCrmMessage.trim() || !editingCrmUser) return;
    
    try {
      await axios.post(`${API}/crm/users/${editingCrmUser.id}/messages`, {
        content: newCrmMessage,
        message_type: crmMessageType
      }, { withCredentials: true });
      
      setNewCrmMessage("");
      fetchCrmMessages(editingCrmUser.id);
      toast.success("Mensaje agregado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar mensaje");
    }
  };

  const handleDeleteCrmMessage = async (messageId) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    
    try {
      await axios.delete(`${API}/crm/messages/${messageId}`, { withCredentials: true });
      fetchCrmMessages(editingCrmUser.id);
      toast.success("Mensaje eliminado");
    } catch (error) {
      toast.error("Error al eliminar mensaje");
    }
  };

  // Load messages when editing a CRM user
  useEffect(() => {
    if (editingCrmUser) {
      fetchCrmMessages(editingCrmUser.id);
    } else {
      setCrmMessages([]);
      setNewCrmMessage("");
    }
  }, [editingCrmUser]);

  // Fetch CRM data when tab changes
  useEffect(() => {
    if (activeTab === "crm") {
      fetchCrmUsers();
      fetchCrmStats();
    }
  }, [activeTab, crmSearchTerm, crmFilterGasto, crmFilterModelo, crmFilterCashback]);

  const handleImportCSV = async () => {
    if (!csvFile) {
      toast.error("Por favor selecciona un archivo");
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', csvFile);

    // Determinar si es CSV o Excel
    const isExcel = csvFile.name.endsWith('.xlsx') || csvFile.name.endsWith('.xls');
    const endpoint = isExcel ? `${API}/import/excel` : `${API}/import/csv`;

    try {
      const response = await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`✅ Importados ${response.data.imported} registros`);
      if (response.data.errors.length > 0) {
        toast.warning(`Errores: ${response.data.errors.length}`);
      }
      setOpenImportDialog(false);
      setCsvFile(null);
      fetchData();
    } catch (error) {
      toast.error("Error al importar archivo");
    } finally {
      setImporting(false);
    }
  };

  // Función para calcular fechas del período quincenal
  const calculatePeriodDates = (year, month, period) => {
    const monthStr = month.toString().padStart(2, '0');
    
    if (period === 1) {
      // Período 1: del 1 al 15
      return {
        fecha_inicio: `${year}-${monthStr}-01`,
        fecha_fin: `${year}-${monthStr}-15`,
        period_name: `Período 1 (1-15 ${getMonthName(month)} ${year})`
      };
    } else {
      // Período 2: del 16 al último día del mes
      // month es 1-12, pero Date espera 0-11, entonces month sin ajustar da el siguiente mes
      // y con día 0 obtenemos el último día del mes actual
      const lastDay = new Date(year, month, 0).getDate();
      return {
        fecha_inicio: `${year}-${monthStr}-16`,
        fecha_fin: `${year}-${monthStr}-${lastDay}`,
        period_name: `Período 2 (16-${lastDay} ${getMonthName(month)} ${year})`
      };
    }
  };

  // Función auxiliar para obtener nombre del mes
  const getMonthName = (month) => {
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return months[month - 1];
  };

  const handleCreateGoal = async () => {
    if (!newGoal.user_id || !newGoal.goal_amount) {
      toast.error("Completa todos los campos (empleado y meta)");
      return;
    }

    // Calcular las fechas basadas en la selección
    const periodDates = calculatePeriodDates(newGoal.year, newGoal.month, newGoal.period);

    try {
      const goalData = {
        user_id: newGoal.user_id,
        goal_amount: newGoal.goal_amount,
        fecha_inicio: periodDates.fecha_inicio,
        fecha_fin: periodDates.fecha_fin,
        period_name: periodDates.period_name
      };
      
      await axios.post(`${API}/goals`, goalData, { withCredentials: true });
      toast.success("Meta asignada exitosamente");
      setOpenGoalDialog(false);
      setNewGoal({ 
        user_id: "", 
        goal_amount: 0, 
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        period: 1,
        fecha_inicio: "",
        fecha_fin: "",
        period_name: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar meta");
    }
  };

  const handleUpdateGoal = async () => {
    if (!editingGoal || !editingGoal.goal_amount) {
      toast.error("La meta es requerida");
      return;
    }

    try {
      await axios.put(`${API}/goals/${editingGoal.id}`, {
        goal_amount: editingGoal.goal_amount,
        month: editingGoal.month,
        fecha_inicio: editingGoal.fecha_inicio,
        fecha_fin: editingGoal.fecha_fin
      }, { withCredentials: true });
      toast.success("Meta actualizada exitosamente");
      setOpenEditGoalDialog(false);
      setEditingGoal(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar meta");
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta meta?")) return;
    
    try {
      await axios.delete(`${API}/goals/${goalId}`, { withCredentials: true });
      toast.success("Meta eliminada exitosamente");
      setOpenEditGoalDialog(false);
      setEditingGoal(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting goal:", error);
      toast.error("Error al eliminar meta: " + (error.response?.data?.detail || error.message));
    }
  };


  const handleUpdateUserName = async () => {
    if (!editingUser || !editingUser.name) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      await axios.put(`${API}/users/${editingUser.id}/name?name=${encodeURIComponent(editingUser.name)}`, {}, { withCredentials: true });

      // Si cambió el username, actualizarlo
      if (editingUser.username && editingUser.username !== editingUser._originalUsername) {
        await axios.put(
          `${API}/users/${editingUser.id}/username?username=${encodeURIComponent(editingUser.username)}`,
          {},
          { withCredentials: true }
        );
      }

      // Si se proporcionó nueva contraseña, resetearla
      if (editingUser.newPassword && editingUser.newPassword.length >= 6) {
        await axios.put(
          `${API}/users/${editingUser.id}/password?password=${encodeURIComponent(editingUser.newPassword)}`,
          {},
          { withCredentials: true }
        );
        toast.success("Contraseña actualizada. El usuario debe iniciar sesión nuevamente.");
      }

      toast.success("Usuario actualizado");
      setOpenEditDialog(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar usuario");
    }
  };

  const handleResetPassword = async (userId, userName) => {
    const newPassword = window.prompt(`Ingresa nueva contraseña para ${userName} (mínimo 6 caracteres):`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      await axios.put(
        `${API}/users/${userId}/password?password=${encodeURIComponent(newPassword)}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Contraseña de ${userName} actualizada. Comparte la nueva contraseña con el usuario.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al resetear contraseña");
    }
  };

  const handleGenerateMagicLink = async (userId, userName) => {
    try {
      const response = await axios.post(
        `${API}/users/${userId}/magic-link`,
        {},
        { withCredentials: true }
      );
      const token = response.data.token;
      const url = `${window.location.origin}/login?token=${token}`;
      // Copiar al portapapeles
      try {
        await navigator.clipboard.writeText(url);
        toast.success(
          `Link copiado al portapapeles. Envíaselo a ${userName} por WhatsApp. Hace clic y entra automáticamente. Válido 7 días.`,
          { duration: 8000 }
        );
      } catch {
        // Fallback: mostrar en prompt para copia manual
        window.prompt(
          `Copia este link y envíalo a ${userName} por WhatsApp. Hace clic y entra automáticamente. Válido 7 días:`,
          url
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar link de acceso");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`¿Estás seguro de eliminar a ${userName}? Esta acción no se puede deshacer.`)) {
      try {
        await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
        toast.success("Usuario eliminado");
        fetchData();
      } catch (error) {
        toast.error("Error al eliminar usuario");
      }
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.role || !newUser.username || !newUser.password) {
      toast.error("Completa todos los campos (email, nombre, username, contraseña y rol)");
      return;
    }

    // Validar contraseña mínima
    if (newUser.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    try {
      await axios.post(
        `${API}/users/create?email=${encodeURIComponent(newUser.email)}&name=${encodeURIComponent(newUser.name)}&role=${newUser.role}&username=${encodeURIComponent(newUser.username)}&password=${encodeURIComponent(newUser.password)}`, 
        {}, 
        { withCredentials: true }
      );
      toast.success("Usuario creado exitosamente");
      setOpenCreateDialog(false);
      setNewUser({ email: "", name: "", role: "chatter", username: "", password: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    }
  };

  // Agenda management functions
  const fetchWeekTasks = async () => {
    try {
      const response = await axios.get(`${API}/agenda/current-week`, { withCredentials: true });
      setWeekTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Error al cargar tareas");
    }
  };

  useEffect(() => {
    if (activeTab === "agenda") {
      fetchWeekTasks();
    }
    if (activeTab === "applications") {
      fetchModelApplications(applicationFilter === "all" ? null : applicationFilter);
    }
    if (activeTab === "nomina") {
      fetchPayslips();
    }
  }, [activeTab, applicationFilter]);

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.day) {
      toast.error("Completa todos los campos");
      return;
    }

    try {
      await axios.post(`${API}/agenda/tasks`, newTask, { withCredentials: true });
      toast.success("Tarea creada exitosamente");
      setOpenTaskDialog(false);
      setNewTask({ title: "", day: "lunes", priority: "normal", task_type: "fija", order: 0 });
      fetchWeekTasks();
    } catch (error) {
      toast.error("Error al crear tarea");
    }
  };


  // Contabilidad functions
  const fetchReportesQuincenales = async () => {
    try {
      const response = await axios.get(`${API}/contabilidad/quincenal/resumen`, {
        params: {
          quincena_inicio: quincenaInicio,
          quincena_fin: quincenaFin
        },
        withCredentials: true
      });
      setResumenQuincenal(response.data);
      
      // Si hay reportes del backend, usarlos directamente
      if (response.data.reportes && response.data.reportes.length > 0) {
        setReportesQuincenales(response.data.reportes);
      } else {
        // Si no hay reportes, inicializar con modelos activos (solo una vez)
        initializeReportesFromModels();
      }
    } catch (error) {
      console.error("Error fetching reportes:", error);
      toast.error("Error al cargar reportes");
    }
  };

  const initializeReportesFromModels = () => {
    // Crear reportes vacíos basados en modelos activos
    if (models.length === 0) {
      setReportesQuincenales([]);
      return;
    }
    
    // Filtrar modelos activos y eliminar duplicados por ID
    const modelosActivos = models.filter(m => m.status === 'activa');
    const modelosUnicos = Array.from(new Map(modelosActivos.map(m => [m.id, m])).values());
    
    const reportesIniciales = modelosUnicos.map(modelo => ({
      modelo_id: modelo.id,
      modelo_name: modelo.name,
      año_ingreso: modelo.año_ingreso || new Date().getFullYear(),
      porcentaje_agencia: modelo.porcentaje_agencia || 40,
      valor_neto: 0,
      valor_neto_modelo: 0,
      valor_neto_agencia: 0
    }));
    
    setReportesQuincenales(reportesIniciales);
  };

  const handleUpdateLocalReporte = (modeloId, field, value) => {
    // Actualizar el reporte localmente sin guardar
    setReportesQuincenales(prev => 
      prev.map(reporte => 
        reporte.modelo_id === modeloId 
          ? { ...reporte, [field]: parseFloat(value) || 0 }
          : reporte
      )
    );
  };

  const handleGuardarReporteQuincenal = async () => {
    try {
      // Guardar todos los reportes con valores
      const reportesConValor = reportesQuincenales.filter(r => r.valor_neto > 0);
      
      if (reportesConValor.length === 0) {
        toast.error("No hay datos para guardar");
        return;
      }

      // Guardar cada reporte
      for (const reporte of reportesConValor) {
        if (reporte.id) {
          // Actualizar existente
          await axios.put(`${API}/contabilidad/quincenal/${reporte.id}`, {
            valor_neto: reporte.valor_neto,
            porcentaje_agencia: reporte.porcentaje_agencia,
            valor_neto_modelo: reporte.valor_neto * ((100 - reporte.porcentaje_agencia) / 100),
            valor_neto_agencia: reporte.valor_neto * (reporte.porcentaje_agencia / 100)
          }, { withCredentials: true });
        } else {
          // Crear nuevo
          await axios.post(`${API}/contabilidad/quincenal`, {
            modelo_id: reporte.modelo_id,
            quincena_inicio: quincenaInicio,
            quincena_fin: quincenaFin,
            porcentaje_agencia: reporte.porcentaje_agencia,
            valor_neto: reporte.valor_neto,
            valor_neto_modelo: reporte.valor_neto * ((100 - reporte.porcentaje_agencia) / 100),
            valor_neto_agencia: reporte.valor_neto * (reporte.porcentaje_agencia / 100)
          }, { withCredentials: true });
        }
      }
      
      toast.success(`Reporte quincenal guardado exitosamente (${reportesConValor.length} modelos)`);
      fetchReportesQuincenales();
    } catch (error) {
      console.error("Error saving reporte:", error);
      toast.error("Error al guardar el reporte");
    }
  };

  const handleCerrarPeriodo = async () => {
    if (!confirm(`¿Cerrar el periodo ${quincenaInicio} a ${quincenaFin}? No podrás editarlo después.`)) {
      return;
    }
    
    try {
      await axios.post(`${API}/contabilidad/quincenal/cerrar-periodo`, null, {
        params: {
          quincena_inicio: quincenaInicio,
          quincena_fin: quincenaFin
        },
        withCredentials: true
      });
      
      toast.success("Periodo cerrado exitosamente");
      fetchReportesQuincenales();
      fetchHistorialPeriodos();
    } catch (error) {
      console.error("Error cerrando periodo:", error);
      toast.error("Error al cerrar el periodo");
    }
  };

  const handleDeletePeriodo = async (periodo) => {
    if (!confirm(`⚠️ ¿Eliminar TODOS los reportes del periodo ${periodo.quincena_inicio} a ${periodo.quincena_fin}?\n\nEsto eliminará permanentemente:\n- ${periodo.cantidad_modelos} reportes de modelos\n- Total de $${periodo.total_valor_neto.toFixed(2)}\n\nEsta acción NO se puede deshacer.`)) {
      return;
    }
    
    try {
      // Obtener todos los reportes del periodo para eliminarlos
      const response = await axios.get(`${API}/contabilidad/quincenal/resumen`, {
        params: {
          quincena_inicio: periodo.quincena_inicio,
          quincena_fin: periodo.quincena_fin
        },
        withCredentials: true
      });
      
      const reportes = response.data.reportes;
      
      // Eliminar cada reporte individualmente
      for (const reporte of reportes) {
        await axios.delete(`${API}/contabilidad/quincenal/${reporte.id}`, {
          withCredentials: true
        });
      }
      
      toast.success(`Periodo eliminado exitosamente (${reportes.length} reportes eliminados)`);
      fetchHistorialPeriodos();
      
      // Si estamos viendo ese periodo, limpiar la tabla
      if (quincenaInicio === periodo.quincena_inicio && quincenaFin === periodo.quincena_fin) {
        fetchReportesQuincenales();
      }
    } catch (error) {
      console.error("Error eliminando periodo:", error);
      toast.error("Error al eliminar el periodo");
    }
  };

  const fetchHistorialPeriodos = async () => {
    try {
      const response = await axios.get(`${API}/contabilidad/quincenal/historial`, {
        withCredentials: true
      });
      setHistorialPeriodos(response.data);
    } catch (error) {
      console.error("Error fetching historial:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "contabilidad" && models.length > 0) {
      fetchReportesQuincenales();
      fetchHistorialPeriodos();
    }
  }, [activeTab, quincenaInicio, quincenaFin, models]);


  const handleDeleteTask = async (taskId) => {
    if (!confirm("¿Eliminar esta tarea?")) return;
    
    try {
      await axios.delete(`${API}/agenda/tasks/${taskId}`, { withCredentials: true });
      toast.success("Tarea eliminada");
      fetchWeekTasks();
    } catch (error) {
      toast.error("Error al eliminar tarea");
    }
  };

  const handleDuplicateWeek = async () => {
    if (!confirm("¿Duplicar todas las tareas de esta semana a la próxima semana?")) return;
    
    try {
      const response = await axios.post(`${API}/agenda/duplicate-week`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Error al duplicar semana");
    }
  };

  // Model management functions
  const handleUpdateModel = async () => {
    if (!editingModel) return;
    
    try {
      await axios.put(`${API}/models/${editingModel.id}`, {
        name: editingModel.name,
        biografia: editingModel.biografia,
        usuarios_potenciales: editingModel.usuarios_potenciales,
        metodologia_chat: editingModel.metodologia_chat,
        expresion_chat: editingModel.expresion_chat,
        status: editingModel.status,
        porcentaje_agencia: editingModel.porcentaje_agencia,
        año_ingreso: editingModel.año_ingreso,
        photo_url: editingModel.photo_url,
        personalidad: editingModel.personalidad,
        social_links: editingModel.social_links || {},
        precio_custom_video: editingModel.precio_custom_video,
        precio_custom_photo: editingModel.precio_custom_photo,
        precio_videollamada: editingModel.precio_videollamada,
        tip_menu: editingModel.tip_menu || [],
        platforms: editingModel.platforms && editingModel.platforms.length ? editingModel.platforms : ["onlyfans"],
      }, { withCredentials: true });
      
      toast.success("Modelo actualizado exitosamente");
      setOpenEditModelDialog(false);
      setEditingModel(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar modelo");
    }
  };

  const handleModelPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La foto debe pesar menos de 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setEditingModel({ ...editingModel, photo_url: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteModel = async (modelId) => {
    if (!confirm("¿Estás seguro de eliminar este modelo?")) return;
    
    try {
      await axios.delete(`${API}/models/${modelId}`, { withCredentials: true });
      toast.success("Modelo eliminado");
      setOpenEditModelDialog(false);
      setEditingModel(null);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar modelo");
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-xl font-semibold text-white">Cargando...</div>
      </div>
    );
  }

  const pieData = [
    { name: 'Subscriptions', value: stats?.total_subs || 0, color: COLORS.subs },
    { name: 'Messages', value: stats?.total_messages || 0, color: COLORS.messages },
    { name: 'Tips', value: stats?.total_tips || 0, color: COLORS.tips },
    { name: 'Posts', value: stats?.total_posts || 10, color: COLORS.posts }
  ];

  const maxEarnings = Math.max(...(stats?.employee_stats?.map(e => e.total_money) || [1]));

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
        onLogout={handleLogout}
        onCreateUser={() => setOpenCreateDialog(true)}
      />

      {/* Main Content - No se desplaza con el sidebar */}
      <div className="w-full">
        {/* Top Bar */}
        <div className="h-16 bg-black/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            {/* Toggle Sidebar Button */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/70">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-semibold text-white">
              {activeTab === 'overview' && 'Dashboard'}
              {activeTab === 'reportes' && 'Reportes Diarios'}
              {activeTab === 'crm' && 'Spenders'}
              {activeTab === 'settings' && 'Empleados'}
              {activeTab === 'models' && 'Modelos'}
              {activeTab === 'contabilidad' && 'Contabilidad'}
              {activeTab === 'asistencia' && 'Asistencia'}
              {activeTab === 'monitoreo' && 'Monitoreo'}
              {activeTab === 'nomina' && 'Nómina'}
              {activeTab === 'agenda' && 'Agenda'}
              {activeTab === 'newsletter' && 'Newsletter'}
              {activeTab === 'jgmpro' && 'JGM PRO'}
              {activeTab === 'chatterly' && 'Chatterly'}
              {activeTab === 'email-aliases' && 'Buzón IG'}
              {activeTab === 'metas' && 'Metas'}
              {activeTab === 'solicitudes' && 'Solicitudes'}
              {activeTab === 'calculadora' && 'Calculadora'}
              {activeTab === 'ventas-mym' && 'Ventas MYM'}
              </h1>
              <p className="text-sm text-gray-500">Bienvenido, {user?.name || user?.email}</p>
            </div>
          </div>
          
          {/* Right side icons - properly organized */}
          <div className="flex items-center gap-3">
            <PushNotificationSetup />
            <NotificationBell />
            
            {/* Chat Button */}
            <button
              onClick={() => setTeamChatOpen(true)}
              className="relative flex items-center justify-center w-10 h-10 rounded-lg bg-[#0a0a0a] border border-white/[0.08] hover:bg-zinc-800 transition-colors"
              title="Chat del Equipo"
            >
              <MessageCircle className="w-5 h-5 text-blue-400" />
              {chatUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                </span>
              )}
            </button>
            
            {/* Profile Button */}
            <button 
              onClick={openProfileEditor}
              className="flex items-center gap-2 hover:bg-[#0a0a0a] border border-white/[0.08] shadow-[0_2px_8px_rgba(0,0,0,0.4)] rounded-lg px-2 py-1 transition-colors cursor-pointer"
              title="Editar Perfil"
            >
              {user?.profile_picture ? (
                <img 
                  src={user.profile_picture} 
                  alt={user?.name || 'Usuario'} 
                  className="w-10 h-10 rounded-full object-cover border-2 border-purple-500"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{user?.name?.charAt(0) || 'A'}</span>
                </div>
              )}
              <span className="text-white text-sm hidden md:block">{user?.name?.split(' ')[0] || 'Usuario'}</span>
            </button>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-6">
          {/* Time Filters - Solo para overview */}
          {activeTab === 'overview' && (
            <div className="flex gap-3 mb-6">
              <Button
                onClick={() => setTimeFilter("yesterday")}
                className={`rounded-lg ${
                  timeFilter === "yesterday" 
                    ? "bg-blue-600 hover:bg-blue-700" 
                    : "bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                }`}
              >
                Ayer
              </Button>
              <Button
                onClick={() => setTimeFilter("today")}
                className={`rounded-lg ${
                  timeFilter === "today" 
                    ? "bg-blue-600 hover:bg-blue-700" 
                    : "bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                }`}
              >
                Hoy
              </Button>
              <Button
                onClick={() => setTimeFilter("week")}
                className={`rounded-lg ${
                  timeFilter === "week" 
                    ? "bg-blue-600 hover:bg-blue-700" 
                    : "bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                }`}
              >
                Esta Semana
              </Button>
              <Button
                onClick={() => setTimeFilter("month")}
                className={`rounded-lg ${
                  timeFilter === "month" 
                    ? "bg-blue-600 hover:bg-blue-700" 
                    : "bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                }`}
              >
                Este Mes
              </Button>
              <Button
                onClick={() => setOpenCustomFilter(true)}
                className={`rounded-lg ${
                  timeFilter === "custom" 
                    ? "bg-purple-600 hover:bg-purple-700" 
                    : "bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                }`}
              >
                Custom
              </Button>
            </div>
          )}

        {/* Custom Filter Dialog */}
        <Dialog open={openCustomFilter} onOpenChange={setOpenCustomFilter}>
          <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Filtro Personalizado</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Filter Type Selector */}
              <div className="flex gap-3">
                <Button
                  onClick={() => setCustomFilterType("date")}
                  className={`flex-1 ${
                    customFilterType === "date"
                      ? "bg-purple-600 hover:bg-purple-700"
                      : "bg-zinc-800 hover:bg-zinc-700"
                  }`}
                >
                  Por Fecha Específica
                </Button>
                <Button
                  onClick={() => setCustomFilterType("month")}
                  className={`flex-1 ${
                    customFilterType === "month"
                      ? "bg-purple-600 hover:bg-purple-700"
                      : "bg-zinc-800 hover:bg-zinc-700"
                  }`}
                >
                  Por Mes
                </Button>
                <Button
                  onClick={() => setCustomFilterType("range")}
                  className={`flex-1 ${
                    customFilterType === "range"
                      ? "bg-purple-600 hover:bg-purple-700"
                      : "bg-zinc-800 hover:bg-zinc-700"
                  }`}
                >
                  Rango de Fechas
                </Button>
              </div>

              {/* Date Range Filter */}
              {customFilterType === "date" && (
                <div className="space-y-4 p-4 bg-zinc-800/50 rounded-lg">
                  <Label className="text-gray-300 text-sm">Selecciona una fecha específica</Label>
                  <Input
                    type="date"
                    value={customDateRange.startDate}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, startDate: e.target.value, endDate: e.target.value })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  />
                </div>
              )}

              {/* Month Filter */}
              {customFilterType === "month" && (
                <div className="space-y-4 p-4 bg-zinc-800/50 rounded-lg">
                  <Label className="text-gray-300 text-sm">Selecciona un mes</Label>
                  <Input
                    type="month"
                    value={customDateRange.startMonth}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-');
                      const firstDay = `${year}-${month}-01`;
                      const lastDay = new Date(year, month, 0).toISOString().split('T')[0];
                      setCustomDateRange({ 
                        ...customDateRange, 
                        startMonth: e.target.value,
                        startDate: firstDay,
                        endDate: lastDay
                      });
                    }}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  />
                </div>
              )}

              {/* Range Filter */}
              {customFilterType === "range" && (
                <div className="space-y-4 p-4 bg-zinc-800/50 rounded-lg">
                  <Label className="text-gray-300 text-sm">Selecciona un rango</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-400 mb-2 block">Desde</Label>
                      <Input
                        type="date"
                        value={customDateRange.startDate}
                        onChange={(e) => setCustomDateRange({ ...customDateRange, startDate: e.target.value })}
                        className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 mb-2 block">Hasta</Label>
                      <Input
                        type="date"
                        value={customDateRange.endDate}
                        onChange={(e) => setCustomDateRange({ ...customDateRange, endDate: e.target.value })}
                        className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Apply Button */}
              <Button
                onClick={() => {
                  setTimeFilter("custom");
                  setOpenCustomFilter(false);
                }}
                className="w-full bg-purple-600 hover:bg-purple-700"
                disabled={!customDateRange.startDate || (customFilterType === "range" && !customDateRange.endDate)}
              >
                Aplicar Filtro
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stats Cards - Floating Glass Design (only on overview) */}
        {activeTab === 'overview' && (
        <>
        {/* Toggle Manual / Chatterly */}
        <div className="mb-3 flex items-center gap-2" data-testid="dashboard-stats-source-toggle">
          <span className="text-[10px] uppercase tracking-widest text-white/40">Fuente de ingresos:</span>
          <div className="inline-flex rounded-lg border border-white/10 bg-black/40 p-0.5">
            <button
              onClick={() => setStatsSource("manual")}
              data-testid="stats-source-manual"
              className={`px-3 py-1 text-xs rounded-md transition ${
                statsSource === "manual"
                  ? "bg-white/10 text-white font-semibold"
                  : "text-white/50 hover:text-white/80"
              }`}
            >
              📋 Manual
            </button>
            <button
              onClick={() => setStatsSource("chatterly")}
              disabled={!chatterlyStats}
              data-testid="stats-source-chatterly"
              className={`px-3 py-1 text-xs rounded-md transition ${
                statsSource === "chatterly"
                  ? "bg-emerald-500/20 text-emerald-300 font-semibold"
                  : "text-white/50 hover:text-white/80 disabled:opacity-30"
              }`}
            >
              📡 Chatterly {chatterlyStats?.month ? `(${chatterlyStats.month})` : ""}
            </button>
          </div>
          {statsSource === "chatterly" && chatterlyStats && (
            <span className="text-[10px] text-emerald-400/70 ml-auto">
              {chatterlyStats.totals.count} eventos · Mes actual
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className="bg-[#0a0a0a] border border-white/[0.08] cursor-pointer hover:border-white/20 hover:-translate-y-1 transition-all duration-300 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl"
            onClick={() => {
              setEmpleadoSeleccionadoVentas(null);
              setOpenCalendarioVentas(true);
            }}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-[0_4px_12px_rgba(251,191,36,0.4)]">
                  <DollarSign className="w-4 h-4 text-white" />
                </div>
                <span className="text-white/60 font-medium">Total</span>
              </div>
              <p className="text-2xl font-bold text-white">${(statsSource === "chatterly" ? (chatterlyStats?.totals?.total || 0) : (stats?.total_money || 0)).toLocaleString()}</p>
              <p className="text-sm text-white/30 mt-1">{statsSource === "chatterly" ? "Chatterly · Mes actual" : "Ver calendario"}</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0a0a0a] border border-white/[0.08] hover:border-white/20 hover:-translate-y-1 transition-all duration-300 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-600 flex items-center justify-center shadow-[0_4px_12px_rgba(217,70,239,0.4)]">
                  <span className="text-white text-xs font-bold">S</span>
                </div>
                <span className="text-white/60 font-medium">Subs</span>
              </div>
              <p className="text-2xl font-bold text-white">${(statsSource === "chatterly" ? (chatterlyStats?.totals?.subs || 0) : (stats?.total_subs || 0)).toLocaleString()}</p>
              <p className="text-sm text-white/30 mt-1">Subscripciones</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0a0a0a] border border-white/[0.08] hover:border-white/20 hover:-translate-y-1 transition-all duration-300 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center shadow-[0_4px_12px_rgba(6,182,212,0.4)]">
                  <span className="text-white text-xs font-bold">M</span>
                </div>
                <span className="text-white/60 font-medium">Messages</span>
              </div>
              <p className="text-2xl font-bold text-white">${(statsSource === "chatterly" ? (chatterlyStats?.totals?.sales || 0) : (stats?.total_messages || 0)).toLocaleString()}</p>
              <p className="text-sm text-white/30 mt-1">PPV & DMs</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0a0a0a] border border-white/[0.08] hover:border-white/20 hover:-translate-y-1 transition-all duration-300 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-[0_4px_12px_rgba(139,92,246,0.4)]">
                  <DollarSign className="w-4 h-4 text-white" />
                </div>
                <span className="text-white/60 font-medium">Tips</span>
              </div>
              <p className="text-2xl font-bold text-white">${(statsSource === "chatterly" ? (chatterlyStats?.totals?.tips || 0) : (stats?.total_tips || 0)).toLocaleString()}</p>
              <p className="text-sm text-white/30 mt-1">Propinas</p>
            </CardContent>
          </Card>
        </div>
        </>
        )}

        {/* Revenue Breakdown - Visual Chart */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Donut Chart */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-4">Distribución</h3>
                <div className="flex items-center justify-center">
                  <div className="relative w-40 h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Subs', value: stats?.total_subs || 0, fill: 'url(#gradientSubs)' },
                            { name: 'Messages', value: stats?.total_messages || 0, fill: 'url(#gradientMessages)' },
                            { name: 'Tips', value: stats?.total_tips || 0, fill: 'url(#gradientTips)' }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={70}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          <Cell fill="#d946ef" />
                          <Cell fill="#06b6d4" />
                          <Cell fill="#8b5cf6" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-white">${((stats?.total_money || 0) / 1000).toFixed(1)}k</span>
                      <span className="text-xs text-gray-400">Total</span>
                    </div>
                  </div>
                </div>
                {/* Legend */}
                <div className="flex justify-center gap-4 mt-4">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-fuchsia-500"></div>
                    <span className="text-xs text-gray-400">Subs</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                    <span className="text-xs text-gray-400">Msgs</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-violet-500"></div>
                    <span className="text-xs text-gray-400">Tips</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bar Comparison */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl lg:col-span-2">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-4">Comparativa por Categoría</h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={[
                        { name: 'Subs', value: stats?.total_subs || 0 },
                        { name: 'Messages', value: stats?.total_messages || 0 },
                        { name: 'Tips', value: stats?.total_tips || 0 }
                      ]}
                      layout="vertical"
                      margin={{ left: 10, right: 40 }}
                      barCategoryGap="25%"
                    >
                      <XAxis type="number" hide />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#9ca3af', fontSize: 13 }}
                        width={70}
                      />
                      <Tooltip
                        cursor={{ fill: 'transparent' }}
                        contentStyle={{
                          backgroundColor: '#18181b',
                          border: '1px solid #3f3f46',
                          borderRadius: '8px',
                          color: 'white',
                          padding: '8px 12px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                        }}
                        formatter={(value) => [`$${value.toLocaleString()}`, '']}
                        labelStyle={{ color: '#9ca3af', marginBottom: '4px' }}
                      />
                      <Bar 
                        dataKey="value" 
                        radius={[0, 6, 6, 0]}
                        barSize={28}
                        background={false}
                      >
                        {[
                          { name: 'Subs', fill: '#d946ef' },
                          { name: 'Messages', fill: '#06b6d4' },
                          { name: 'Tips', fill: '#8b5cf6' }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Stats below */}
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
                  <div className="text-center">
                    <div className="text-lg font-bold text-fuchsia-400">${(stats?.total_subs || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Subs</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-cyan-400">${(stats?.total_messages || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Messages</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-violet-400">${(stats?.total_tips || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Tips</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Content Tabs - Hidden, now using sidebar */}

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <>
            {/* Migration Banner - Solo se muestra si hay datos para migrar */}
            {migrationStatus?.migracion_necesaria && (
              <Card className="bg-yellow-900/20 border border-yellow-500/30 rounded-2xl mb-6">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-yellow-400 font-medium">Migracion de datos necesaria</h3>
                      <p className="text-yellow-200/70 text-sm">
                        Hay {migrationStatus.necesitan_migracion} reportes historicos que necesitan actualizarse para mostrar correctamente en las categorias.
                      </p>
                    </div>
                    <Button 
                      onClick={runMigration} 
                      disabled={isMigrating}
                      className="bg-yellow-600 hover:bg-yellow-700 text-black font-medium"
                    >
                      {isMigrating ? "Migrando..." : "Migrar Datos"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Banner para limpiar duplicados */}
            {showCleanupButton && (
              <Card className="bg-red-900/20 border border-red-500/30 rounded-2xl mb-6">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-red-400 font-medium">Corregir totales de facturacion</h3>
                      <p className="text-red-200/70 text-sm">
                        Si los totales del mes no coinciden, presiona este boton para eliminar reportes duplicados.
                      </p>
                    </div>
                    <Button 
                      onClick={cleanupDuplicates} 
                      disabled={isCleaningDuplicates}
                      className="bg-red-600 hover:bg-red-700 text-white font-medium"
                    >
                      {isCleaningDuplicates ? "Limpiando..." : "Limpiar Duplicados"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Performance Over Time - Full Width */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl mb-6">
              <CardHeader>
                <CardTitle className="text-white">Performance Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats?.daily_data || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#71717a"
                        tick={{ fill: '#71717a', fontSize: 12 }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getDate()}/${date.getMonth() + 1}`;
                        }}
                      />
                      <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#18181b',
                          border: '1px solid #27272a',
                          borderRadius: '8px',
                          color: 'white'
                        }}
                      />
                      <Line type="monotone" dataKey="total" stroke={COLORS.total} strokeWidth={3} dot={false} name="Total Earnings" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Empleados - Full Width con Barras Horizontales */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Top Empleados</CardTitle>
                    <p className="text-sm text-gray-500">Ranking por ventas del periodo</p>
                  </div>
                  <span className="text-xs text-gray-400 bg-zinc-800/80 px-3 py-1 rounded-full">
                    {timeFilter === 'today' ? 'Hoy' : timeFilter === 'yesterday' ? 'Ayer' : timeFilter === 'week' ? 'Esta Semana' : timeFilter === 'month' ? 'Este Mes' : 'Personalizado'}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {topEmpleados.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {topEmpleados.slice(0, 5).map((emp, index) => {
                      const maxVentas = topEmpleados[0]?.total_ventas || 1;
                      const percentage = (emp.total_ventas / maxVentas) * 100;
                      return (
                        <div key={emp.user_id} className="bg-zinc-900/50 rounded-xl p-4 border border-white/5 hover:border-white/10 transition-all">
                          <div className="flex items-center gap-3 mb-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-black shadow-lg shadow-yellow-500/30' :
                              index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-black' :
                              index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-black' :
                              'bg-zinc-700 text-white'
                            }`}>
                              {index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{emp.user_name}</p>
                              <p className="text-xs text-gray-500">{emp.total_reportes} reportes</p>
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-green-400 mb-2">${emp.total_ventas.toLocaleString()}</div>
                          <div className="relative h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-2">
                            <div 
                              className={`absolute left-0 top-0 h-full rounded-full transition-all duration-700 ${
                                index === 0 ? 'bg-gradient-to-r from-yellow-500 to-amber-400' :
                                index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-300' :
                                index === 2 ? 'bg-gradient-to-r from-orange-500 to-orange-400' :
                                'bg-gradient-to-r from-blue-600 to-cyan-400'
                              }`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>Msgs: ${emp.messages?.toFixed(0) || 0}</span>
                            <span>Tips: ${emp.tips?.toFixed(0) || 0}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <p>No hay reportes de empleados en este periodo</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Resumen Facturación + Gráfico Mensual */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Revenue Summary Card */}
              <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-white">💰 Resumen de Facturación</CardTitle>
                  <div className="flex gap-2 mt-3">
                    <Input
                      type="date"
                      value={statsDateRange.start}
                      onChange={(e) => setStatsDateRange({ ...statsDateRange, start: e.target.value })}
                      className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white text-sm"
                    />
                    <Input
                      type="date"
                      value={statsDateRange.end}
                      onChange={(e) => setStatsDateRange({ ...statsDateRange, end: e.target.value })}
                      className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white text-sm"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {totalPeriodStats ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                        <div className="text-gray-400 text-xs mb-1">Total Facturado</div>
                        <div className="text-2xl font-bold text-white">${totalPeriodStats.total_valor_neto.toFixed(0)}</div>
                        <div className="text-xs text-gray-500 mt-1">{totalPeriodStats.cantidad_meses} meses</div>
                      </div>
                      <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                        <div className="text-gray-400 text-xs mb-1">Promedio Mes</div>
                        <div className="text-2xl font-bold text-purple-400">
                          ${totalPeriodStats.cantidad_meses > 0 
                            ? (totalPeriodStats.total_valor_neto / totalPeriodStats.cantidad_meses).toFixed(0)
                            : '0'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">promedio</div>
                      </div>
                      <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                        <div className="text-gray-400 text-xs mb-1">Total Modelos</div>
                        <div className="text-2xl font-bold text-blue-400">${totalPeriodStats.total_valor_neto_modelos.toFixed(0)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {totalPeriodStats.total_valor_neto > 0 
                            ? `${((totalPeriodStats.total_valor_neto_modelos / totalPeriodStats.total_valor_neto) * 100).toFixed(1)}%`
                            : '0%'}
                        </div>
                      </div>
                      <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                        <div className="text-gray-400 text-xs mb-1">Total Agencia</div>
                        <div className="text-2xl font-bold text-green-400">${totalPeriodStats.total_valor_neto_agencia.toFixed(0)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {totalPeriodStats.total_valor_neto > 0 
                            ? `${((totalPeriodStats.total_valor_neto_agencia / totalPeriodStats.total_valor_neto) * 100).toFixed(1)}%`
                            : '0%'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 py-16">
                      No hay datos
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Monthly Chart */}
              <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-white">📊 Facturación Mensual</CardTitle>
                </CardHeader>
                <CardContent>
                  {monthlyStats.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthlyStats}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis 
                            dataKey="mes" 
                            stroke="#71717a"
                            tick={{ fill: '#71717a', fontSize: 11 }}
                            tickFormatter={(value) => {
                              const [year, month] = value.split('-');
                              const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                              return `${monthNames[parseInt(month) - 1]}`;
                            }}
                          />
                          <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#18181b',
                              border: '1px solid #27272a',
                              borderRadius: '8px',
                              color: 'white'
                            }}
                            formatter={(value) => `$${value.toFixed(2)}`}
                            labelFormatter={(label) => {
                              const [year, month] = label.split('-');
                              const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                              return `${monthNames[parseInt(month) - 1]} ${year}`;
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="total_valor_neto" 
                            stroke="#10b981" 
                            strokeWidth={3} 
                            dot={{ fill: '#10b981', r: 4 }} 
                            name="Total" 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="total_valor_neto_agencia" 
                            stroke="#a855f7" 
                            strokeWidth={2} 
                            dot={{ fill: '#a855f7', r: 3 }} 
                            name="Agencia" 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="total_valor_neto_modelos" 
                            stroke="#3b82f6" 
                            strokeWidth={2} 
                            dot={{ fill: '#3b82f6', r: 3 }} 
                            name="Modelos" 
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 py-16">
                      <p className="text-sm">No hay datos de facturación</p>
                      <p className="text-xs mt-2">Cierra períodos en Contabilidad</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Top Models de la Quincena */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl mb-6">
              <CardHeader>
                <CardTitle className="text-white">🏆 Top 5 Modelos de la Quincena</CardTitle>
                <p className="text-sm text-gray-400 mt-2">
                  {topModels[0]?.quincena || `${new Date().getDate() <= 15 ? '1-15' : '16-' + new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()} ${new Date().toLocaleDateString('es', { month: 'long', year: 'numeric' })}`}
                </p>
              </CardHeader>
              <CardContent>
                {topModels.length > 0 ? (
                  <div className="space-y-3">
                    {topModels.map((model, index) => (
                      <div 
                        key={model.modelo_id} 
                        className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          {/* Rank Badge */}
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                            index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-black' :
                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black' :
                            index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-black' :
                            'bg-purple-600 text-white'
                          }`}>
                            {index === 0 ? '👑' : `#${model.rank}`}
                          </div>
                          
                          {/* Model Info */}
                          <div>
                            <div className="text-white font-bold text-lg">{model.modelo_name}</div>
                            <div className="text-gray-400 text-sm">{model.cantidad_reportes} reportes cerrados</div>
                          </div>
                        </div>

                        {/* Earnings */}
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-400">${model.total_facturado.toLocaleString()}</div>
                          <div className="text-xs text-gray-400 mt-1">
                            Agencia: ${model.total_agencia.toLocaleString()} | Modelo: ${model.total_modelo.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-16">
                    <p className="text-sm">No hay datos de facturación</p>
                    <p className="text-xs mt-2">Cierra períodos en Contabilidad para ver el ranking</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Neto de Facturación por Quincena */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl mb-6">
              <CardHeader>
                <CardTitle className="text-white">Neto de Facturacion por Quincena</CardTitle>
                <p className="text-sm text-gray-400">Cierres de contabilidad del mes actual</p>
              </CardHeader>
              <CardContent>
                {netoQuincenas.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {netoQuincenas.map((q, index) => (
                      <div key={index} className="bg-zinc-800/50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-white font-semibold">{q.periodo}</span>
                          <span className="text-xs text-gray-400">{q.cantidad_cierres} cierres</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-400 text-sm">Bruto:</span>
                            <span className="text-white font-medium">${q.total_bruto.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400 text-sm">Neto:</span>
                            <span className="text-green-400 font-bold text-lg">${q.total_neto.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-xs pt-2 border-t border-zinc-700">
                            <span className="text-gray-500">Agencia: ${q.total_agencia.toLocaleString()}</span>
                            <span className="text-gray-500">Modelo: ${q.total_modelo.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-8">
                    <p className="text-sm">No hay cierres de quincena</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Goals Management */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Comisiones del Equipo</CardTitle>
              <div className="flex gap-2">
                <Button
                  onClick={() => setActiveTab('metas')}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="goto-metas-tab-btn"
                >
                  Ver Metas →
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Comisiones Card */}
              {comisiones && comisiones.chatters && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-white font-bold text-xl">Comisiones de Chatters</h3>
                    <span className="text-gray-400 text-sm ml-2">(5% del Neto Agencia)</span>
                  </div>
                  <div className="grid grid-cols-1 gap-6 max-w-2xl mx-auto">
                    {comisiones.chatters.map((chatter) => (
                      <Card key={chatter.user_id} className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl hover:border-green-500/50 transition-all">
                        <CardContent className="p-6">
                          {/* Header con nombre */}
                          <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-700">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                                <span className="text-green-400 text-lg">👤</span>
                              </div>
                              <h4 className="text-white font-bold text-lg">{chatter.user_name}</h4>
                            </div>
                          </div>
                          
                          {/* Datos en tabla limpia */}
                          <div className="space-y-4">
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Total Generado</div>
                              <div className="text-2xl font-bold text-white">${chatter.total_generado.toFixed(0)}</div>
                            </div>
                            
                            <div className="pt-3 border-t border-zinc-700/50">
                              <div className="text-xs text-gray-500 mb-1">Neto Agencia (40%)</div>
                              <div className="text-xl font-bold text-blue-400">${chatter.neto_agencia.toFixed(0)}</div>
                            </div>
                            
                            <div className="pt-3 border-t border-zinc-700/50">
                              <div className="text-xs text-gray-500 mb-1">Comisión (5%)</div>
                              <div className="text-2xl font-bold text-green-400">${chatter.comision_chatter.toFixed(2)}</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Metas Individuales movidas a la sección "Metas" del sidebar */}
            </CardContent>
          </Card>
          </>
        )}

        {/* Assign Goal Dialog */}
        <Dialog open={openGoalDialog} onOpenChange={setOpenGoalDialog}>
          <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Asignar Meta</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Empleado</Label>
                <Select
                  value={newGoal.user_id}
                  onValueChange={(value) => setNewGoal({ ...newGoal, user_id: value })}
                >
                  <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                    <SelectValue placeholder="Selecciona un empleado" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                    {users.filter(u => u.role !== 'administrativo').map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Meta ($)</Label>
                <Input
                  type="number"
                  value={newGoal.goal_amount}
                  onChange={(e) => setNewGoal({ ...newGoal, goal_amount: parseFloat(e.target.value) })}
                  className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  placeholder="5000"
                />
              </div>
              
              <div className="space-y-3">
                <Label className="text-gray-300">Período Quincenal</Label>
                
                {/* Año */}
                <div>
                  <Label className="text-xs text-gray-500">Año</Label>
                  <Select
                    value={newGoal.year.toString()}
                    onValueChange={(value) => setNewGoal({ ...newGoal, year: parseInt(value) })}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                      {[2024, 2025, 2026, 2027].map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Mes */}
                <div>
                  <Label className="text-xs text-gray-500">Mes</Label>
                  <Select
                    value={newGoal.month.toString()}
                    onValueChange={(value) => setNewGoal({ ...newGoal, month: parseInt(value) })}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                      {[
                        { value: 1, label: 'Enero' },
                        { value: 2, label: 'Febrero' },
                        { value: 3, label: 'Marzo' },
                        { value: 4, label: 'Abril' },
                        { value: 5, label: 'Mayo' },
                        { value: 6, label: 'Junio' },
                        { value: 7, label: 'Julio' },
                        { value: 8, label: 'Agosto' },
                        { value: 9, label: 'Septiembre' },
                        { value: 10, label: 'Octubre' },
                        { value: 11, label: 'Noviembre' },
                        { value: 12, label: 'Diciembre' }
                      ].map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Período (1 o 2) */}
                <div>
                  <Label className="text-xs text-gray-500 mb-2 block">Período</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setNewGoal({ ...newGoal, period: 1 })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        newGoal.period === 1
                          ? 'bg-purple-600 border-purple-500 text-white'
                          : 'bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-gray-300 hover:border-zinc-600'
                      }`}
                    >
                      <div className="font-semibold">Período 1</div>
                      <div className="text-xs mt-1">1 al 15</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewGoal({ ...newGoal, period: 2 })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        newGoal.period === 2
                          ? 'bg-purple-600 border-purple-500 text-white'
                          : 'bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-gray-300 hover:border-zinc-600'
                      }`}
                    >
                      <div className="font-semibold">Período 2</div>
                      <div className="text-xs mt-1">16 al 30/31</div>
                    </button>
                  </div>
                </div>

                {/* Preview del período seleccionado */}
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Período seleccionado:</p>
                  <p className="text-sm text-white font-medium mt-1">
                    {(() => {
                      if (!newGoal.year || !newGoal.month) return 'Selecciona año y mes';
                      const dates = calculatePeriodDates(newGoal.year, newGoal.month, newGoal.period);
                      const lastDay = dates.fecha_fin.split('-')[2];
                      return newGoal.period === 1 
                        ? `1-15 de ${getMonthName(newGoal.month)} ${newGoal.year}`
                        : `16-${lastDay} de ${getMonthName(newGoal.month)} ${newGoal.year}`;
                    })()}
                  </p>
                </div>
              </div>

              <Button
                onClick={handleCreateGoal}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Asignar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Goal Dialog */}
        <Dialog open={openEditGoalDialog} onOpenChange={setOpenEditGoalDialog}>
          <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl text-white">
            <DialogHeader>
              <DialogTitle className="text-white">Editar Meta: {editingGoal?.user_name}</DialogTitle>
            </DialogHeader>
            {editingGoal && (
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Meta ($)</Label>
                  <Input
                    type="number"
                    value={editingGoal.goal_amount || ""}
                    onChange={(e) => setEditingGoal({ ...editingGoal, goal_amount: parseFloat(e.target.value) })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    placeholder="5000"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Periodo de la Meta</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-500">Fecha Inicio</Label>
                      <Input
                        type="date"
                        value={editingGoal.fecha_inicio || ""}
                        onChange={(e) => setEditingGoal({ ...editingGoal, fecha_inicio: e.target.value })}
                        className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Fecha Fin</Label>
                      <Input
                        type="date"
                        value={editingGoal.fecha_fin || ""}
                        onChange={(e) => setEditingGoal({ ...editingGoal, fecha_fin: e.target.value })}
                        className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Define el periodo de tiempo para alcanzar esta meta</p>
                </div>

                <div className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_2px_8px_rgba(0,0,0,0.4)] rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Progreso Actual:</span>
                    <span className="text-green-400 font-bold">${editingGoal.current_amount?.toFixed(0) || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Porcentaje:</span>
                    <span className="text-white font-bold">{Math.round((editingGoal.current_amount / editingGoal.goal_amount) * 100)}%</span>
                  </div>
                  {editingGoal.current_hours > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Horas Trabajadas:</span>
                      <span className="text-blue-400 font-bold">⏱️ {editingGoal.current_hours?.toFixed(1)}h</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleUpdateGoal}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    💾 Guardar Cambios
                  </Button>
                  <Button
                    onClick={() => handleDeleteGoal(editingGoal.id)}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-900/20"
                  >
                    🗑️ Eliminar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Models Tab */}
        {activeTab === "models" && (
          <div className="space-y-6">
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Gestión de Modelos</CardTitle>
                <Dialog open={openModelDialog} onOpenChange={setOpenModelDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-model-button" className="bg-purple-600 hover:bg-purple-700">
                      ➕ Agregar Modelo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl text-white max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-white">Nuevo Modelo</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="model-name" className="text-gray-300">Nombre del Modelo *</Label>
                        <Input
                          id="model-name"
                          data-testid="model-name-input"
                          value={newModel.name}
                          onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                          className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                          placeholder="Ej: Ana Maria"
                        />
                      </div>
                      <Button
                        data-testid="create-model-button"
                        onClick={handleCreateModel}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        Crear Modelo
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {models.length === 0 ? (
                    <div className="col-span-full text-center text-gray-400 py-8">
                      No hay modelos creados. Agrega tu primer modelo.
                    </div>
                  ) : (
                    models.map((model) => (
                      <div
                        key={model.id}
                        className="p-4 bg-zinc-800 border-2 border-zinc-700 rounded-xl hover:border-purple-500 transition-all relative group"
                      >
                        <div 
                          onClick={() => {
                            setEditingModel(model);
                            setOpenEditModelDialog(true);
                          }}
                          className="cursor-pointer"
                        >
                          <div className="flex items-start gap-3 mb-3">
                            {model.photo_url ? (
                              <img
                                src={model.photo_url}
                                alt={model.name}
                                className="w-16 h-16 rounded-xl object-cover border-2 border-pink-500/40 shrink-0"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-xl bg-zinc-700 border-2 border-zinc-600 flex items-center justify-center text-2xl shrink-0">
                                👤
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start gap-2">
                                <h3 className="text-white font-bold text-lg truncate">{model.name}</h3>
                                <span className={`text-xs px-2 py-1 rounded shrink-0 ${
                                  model.status === 'activa' 
                                    ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                                    : 'bg-red-900/30 text-red-400 border border-red-500/30'
                                }`}>
                                  {model.status}
                                </span>
                              </div>
                              {model.personalidad && (
                                <p className="text-pink-300 text-xs line-clamp-1 mt-0.5 italic">
                                  🎭 {model.personalidad}
                                </p>
                              )}
                            </div>
                          </div>
                        {model.biografia && (
                          <p className="text-gray-400 text-sm line-clamp-2 mb-2">
                            {model.biografia}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {model.porcentaje_agencia && (
                            <span className="text-xs px-2 py-1 rounded bg-green-900/30 text-green-300 border border-green-500/30">
                              💰 {model.porcentaje_agencia}% Agencia
                            </span>
                          )}
                          {(model.precio_custom_video > 0 || model.precio_custom_photo > 0 || model.precio_videollamada > 0) && (
                            <span className="text-xs px-2 py-1 rounded bg-pink-900/30 text-pink-300 border border-pink-500/30">
                              💵 Tarifas
                            </span>
                          )}
                          {model.social_links && Object.values(model.social_links).some(v => v) && (
                            <span className="text-xs px-2 py-1 rounded bg-cyan-900/30 text-cyan-300 border border-cyan-500/30">
                              🔗 Redes
                            </span>
                          )}
                          {model.usuarios_potenciales && (
                            <span className="text-xs px-2 py-1 rounded bg-blue-900/30 text-blue-300 border border-blue-500/30">
                              👥 Usuarios
                            </span>
                          )}
                          {model.metodologia_chat && (
                            <span className="text-xs px-2 py-1 rounded bg-purple-900/30 text-purple-300 border border-purple-500/30">
                              💬 Método
                            </span>
                          )}
                        </div>
                        </div>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`¿Estás seguro de eliminar a ${model.name}?`)) {
                              handleDeleteModel(model.id);
                            }
                          }}
                          variant="destructive"
                          size="sm"
                          className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 hover:bg-red-700 text-white"
                        >
                          🗑️ Eliminar
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}


        {/* Edit Model Dialog */}
        <Dialog open={openEditModelDialog} onOpenChange={setOpenEditModelDialog}>
          <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white text-xl">Editar Modelo: {editingModel?.name}</DialogTitle>
            </DialogHeader>
            {editingModel && (
              <div className="space-y-4">
                {/* ============ FOTO ============ */}
                <div className="flex flex-col items-center gap-3 pb-4 border-b border-white/10">
                  <div className="relative">
                    {editingModel.photo_url ? (
                      <img
                        src={editingModel.photo_url}
                        alt={editingModel.name}
                        className="w-32 h-32 rounded-2xl object-cover border-2 border-pink-500/50"
                        data-testid="model-photo-preview"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-2xl bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center text-gray-500 text-xs text-center px-2">
                        Sin foto
                      </div>
                    )}
                  </div>
                  <label className="cursor-pointer text-pink-400 hover:text-pink-300 text-sm" data-testid="upload-model-photo-label">
                    📷 {editingModel.photo_url ? 'Cambiar foto' : 'Subir foto'}
                    <input type="file" accept="image/*" onChange={handleModelPhotoUpload} className="hidden" />
                  </label>
                  {editingModel.photo_url && (
                    <button
                      type="button"
                      onClick={() => setEditingModel({ ...editingModel, photo_url: null })}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Quitar foto
                    </button>
                  )}
                </div>

                <div>
                  <Label className="text-gray-300">Nombre del Modelo *</Label>
                  <Input
                    value={editingModel.name || ""}
                    onChange={(e) => setEditingModel({ ...editingModel, name: e.target.value })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    placeholder="Ej: Ana Maria"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Estado</Label>
                  <Select 
                    value={editingModel.status || "activa"} 
                    onValueChange={(val) => setEditingModel({ ...editingModel, status: val })}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                      <SelectItem value="activa">Activa</SelectItem>
                      <SelectItem value="inactiva">Inactiva</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300">🌐 Plataformas activas</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[
                      { id: "onlyfans", label: "OnlyFans", color: "from-blue-500 to-cyan-500" },
                      { id: "mym_fans", label: "MYM Fans", color: "from-pink-500 to-rose-500" },
                      { id: "pornhub", label: "PornHub", color: "from-orange-500 to-amber-500" },
                    ].map((pf) => {
                      const list = editingModel.platforms && editingModel.platforms.length ? editingModel.platforms : ["onlyfans"];
                      const active = list.includes(pf.id);
                      return (
                        <button
                          key={pf.id}
                          type="button"
                          data-testid={`platform-${pf.id}`}
                          onClick={() => {
                            const next = active ? list.filter((x) => x !== pf.id) : [...list, pf.id];
                            setEditingModel({ ...editingModel, platforms: next.length ? next : ["onlyfans"] });
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${
                            active
                              ? `bg-gradient-to-r ${pf.color} text-white border-transparent shadow`
                              : "bg-zinc-800 border-zinc-700 text-gray-400 hover:border-purple-500/50"
                          }`}
                        >
                          {active ? "✓ " : ""}{pf.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">OnlyFans es la plataforma por defecto. Marca MYM Fans para que los chatters puedan reportar ventas en €.</p>
                </div>

                <div>
                  <Label className="text-gray-300">Porcentaje para la Agencia (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={editingModel.porcentaje_agencia || ""}
                    onChange={(e) => setEditingModel({ ...editingModel, porcentaje_agencia: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    placeholder="Ej: 30"
                  />
                  <p className="text-xs text-gray-500 mt-1">Porcentaje de comisión que paga la modelo a la agencia</p>
                </div>

                <div>
                  <Label className="text-gray-300">Año de Ingreso</Label>
                  <Input
                    type="number"
                    min="2020"
                    max="2030"
                    value={editingModel.año_ingreso || ""}
                    onChange={(e) => setEditingModel({ ...editingModel, año_ingreso: parseInt(e.target.value) || new Date().getFullYear() })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    placeholder="2024"
                  />
                  <p className="text-xs text-gray-500 mt-1">Año en que la modelo ingresó a la agencia</p>
                </div>

                {/* ============ PERSONALIDAD ============ */}
                <div>
                  <Label className="text-gray-300">🎭 Personalidad</Label>
                  <textarea
                    value={editingModel.personalidad || ""}
                    onChange={(e) => setEditingModel({ ...editingModel, personalidad: e.target.value })}
                    className="w-full min-h-[80px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                    placeholder="Cómo es ella: alegre, tímida, juguetona, dominante, dulce..."
                    data-testid="model-personalidad-input"
                  />
                </div>

                {/* ============ REDES SOCIALES ============ */}
                <div className="space-y-2 p-3 bg-pink-500/5 border border-pink-500/20 rounded-lg">
                  <Label className="text-pink-300">🔗 Redes Sociales / Links</Label>
                  {['instagram', 'tiktok', 'twitter', 'onlyfans', 'fansly', 'otro'].map((plat) => (
                    <div key={plat} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-20 capitalize">{plat}:</span>
                      <Input
                        value={(editingModel.social_links || {})[plat] || ''}
                        onChange={(e) => setEditingModel({
                          ...editingModel,
                          social_links: { ...(editingModel.social_links || {}), [plat]: e.target.value }
                        })}
                        placeholder={`https://...`}
                        className="bg-zinc-800 border-zinc-700 text-white text-sm h-9"
                        data-testid={`social-link-${plat}`}
                      />
                    </div>
                  ))}
                </div>

                {/* ============ TARIFAS ============ */}
                <div className="space-y-3 p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                  <Label className="text-green-300">💰 Tarifas por Servicio (USD)</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-gray-300 text-xs">Custom Video</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingModel.precio_custom_video || 0}
                        onChange={(e) => setEditingModel({ ...editingModel, precio_custom_video: parseFloat(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="model-precio-video"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 text-xs">Custom Foto</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingModel.precio_custom_photo || 0}
                        onChange={(e) => setEditingModel({ ...editingModel, precio_custom_photo: parseFloat(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="model-precio-photo"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 text-xs">Videollamada</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingModel.precio_videollamada || 0}
                        onChange={(e) => setEditingModel({ ...editingModel, precio_videollamada: parseFloat(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="model-precio-call"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    💡 Se autorrellenan al crear solicitudes para esta modelo.
                  </p>
                </div>

                {/* ============ TIP MENU ============ */}
                <div className="space-y-3 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="text-yellow-300">💵 Tip Menu</Label>
                    <button
                      type="button"
                      onClick={() => {
                        const current = editingModel.tip_menu || [];
                        setEditingModel({ ...editingModel, tip_menu: [...current, { nombre: '', precio: 0, precio_max: 0 }] });
                      }}
                      className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
                      data-testid="add-tip-menu-item"
                    >
                      + Agregar acción
                    </button>
                  </div>
                  {(editingModel.tip_menu || []).length === 0 ? (
                    <p className="text-xs text-gray-500 italic">Sin acciones definidas. Click en &quot;+ Agregar acción&quot;.</p>
                  ) : (
                    <div className="space-y-2">
                      {(editingModel.tip_menu || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            value={item.nombre || ''}
                            onChange={(e) => {
                              const tm = [...(editingModel.tip_menu || [])];
                              tm[idx] = { ...tm[idx], nombre: e.target.value };
                              setEditingModel({ ...editingModel, tip_menu: tm });
                            }}
                            placeholder="Ej: BJ, Anal, Strip, Dirty Talk..."
                            className="bg-zinc-800 border-zinc-700 text-white flex-1 min-w-0"
                          />
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-gray-400 text-sm">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.precio || 0}
                              onChange={(e) => {
                                const tm = [...(editingModel.tip_menu || [])];
                                tm[idx] = { ...tm[idx], precio: parseFloat(e.target.value) || 0 };
                                setEditingModel({ ...editingModel, tip_menu: tm });
                              }}
                              placeholder="min"
                              className="bg-zinc-800 border-zinc-700 text-white w-20"
                            />
                            <span className="text-gray-500 text-xs">a</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.precio_max || 0}
                              onChange={(e) => {
                                const tm = [...(editingModel.tip_menu || [])];
                                tm[idx] = { ...tm[idx], precio_max: parseFloat(e.target.value) || 0 };
                                setEditingModel({ ...editingModel, tip_menu: tm });
                              }}
                              placeholder="max"
                              className="bg-zinc-800 border-zinc-700 text-white w-20"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const tm = (editingModel.tip_menu || []).filter((_, i) => i !== idx);
                              setEditingModel({ ...editingModel, tip_menu: tm });
                            }}
                            className="text-red-400 hover:text-red-300 shrink-0 px-2"
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 italic">
                    💡 Lista de acciones con su precio. Los chatters podrán ver esto sin editar.
                  </p>
                </div>

                <div>
                  <Label className="text-gray-300">Biografía del Modelo</Label>
                  <textarea
                    value={editingModel.biografia || ""}
                    onChange={(e) => setEditingModel({ ...editingModel, biografia: e.target.value })}
                    className="w-full min-h-[100px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                    placeholder="Describe la personalidad, edad, gustos, estilo de vida de la modelo..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Historia y personalidad de la modelo</p>
                </div>

                <div>
                  <Label className="text-gray-300">Usuarios Potenciales</Label>
                  <textarea
                    value={editingModel.usuarios_potenciales || ""}
                    onChange={(e) => setEditingModel({ ...editingModel, usuarios_potenciales: e.target.value })}
                    className="w-full min-h-[100px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                    placeholder="Tipo de usuarios que más interactúan, edades, intereses, países..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Perfil del público objetivo</p>
                </div>

                <div>
                  <Label className="text-gray-300">Metodología de Chat</Label>
                  <textarea
                    value={editingModel.metodologia_chat || ""}
                    onChange={(e) => setEditingModel({ ...editingModel, metodologia_chat: e.target.value })}
                    className="w-full min-h-[100px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                    placeholder="Estrategias de conversación, temas a abordar, técnicas de venta..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Cómo llevar las conversaciones</p>
                </div>

                <div>
                  <Label className="text-gray-300">Expresión del Modelo en el Chat</Label>
                  <textarea
                    value={editingModel.expresion_chat || ""}
                    onChange={(e) => setEditingModel({ ...editingModel, expresion_chat: e.target.value })}
                    className="w-full min-h-[100px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                    placeholder="Tono de voz, emojis a usar, estilo de escritura, expresiones características..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Cómo se expresa la modelo</p>
                </div>

                <ModelCredentialsSection
                  modelId={editingModel.id}
                  modelName={editingModel.name}
                />

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleUpdateModel}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    💾 Guardar Cambios
                  </Button>
                  <Button
                    onClick={() => handleDeleteModel(editingModel.id)}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-900/20"
                  >
                    🗑️ Eliminar Modelo
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>



        {/* Asistencia Tab */}
        {activeTab === "asistencia" && (
          <div className="space-y-6">
            <CalendarioAsistencia />
          </div>
        )}

        {/* Monitoreo Tab */}
        {activeTab === "monitoreo" && (
          <div className="space-y-6">
            {/* Panel de Personas Trabajando */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Equipo en Turno
                  <span className="ml-2 px-2 py-1 bg-green-600/20 text-green-400 text-sm rounded-full">
                    {activeSessions.length} activos
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activeSessions.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay empleados trabajando en este momento</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeSessions.map((session) => (
                      <div 
                        key={session.id} 
                        className={`p-4 rounded-lg border ${
                          session.status === 'working' 
                            ? 'bg-green-900/20 border-green-700/50' 
                            : 'bg-orange-900/20 border-orange-700/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            session.status === 'working' ? 'bg-green-600' : 'bg-orange-600'
                          }`}>
                            <span className="text-white font-bold">
                              {session.user_name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-medium">{session.user_name}</p>
                            <p className="text-xs text-gray-400">
                              {session.status === 'working' ? '💼 Trabajando' : '☕ En Break'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${
                              session.status === 'working' ? 'text-green-400' : 'text-orange-400'
                            }`}>
                              {session.status === 'working' ? 'Activo' : 'Break'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Resumen */}
                <div className="mt-6 pt-4 border-t border-zinc-800 grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{activeSessions.length}</p>
                    <p className="text-xs text-gray-400">Total Activos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">
                      {activeSessions.filter(s => s.status === 'working').length}
                    </p>
                    <p className="text-xs text-gray-400">Trabajando</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-400">
                      {activeSessions.filter(s => s.status === 'on_break').length}
                    </p>
                    <p className="text-xs text-gray-400">En Break</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Camaras en Vivo */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                    Camaras en Vivo
                  </CardTitle>
                  <span className="text-sm text-gray-400">{liveFeeds.length} activas</span>
                </div>
              </CardHeader>
              <CardContent>
                {liveFeeds.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No hay camaras activas en este momento</p>
                    <p className="text-sm">Los chatters deben iniciar turno para activar la camara</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {liveFeeds.map((feed) => (
                      <div key={feed.user_id} className="bg-zinc-900 rounded-xl overflow-hidden border border-white/5">
                        <div className="relative aspect-video bg-black">
                          {feed.frame ? (
                            <img 
                              src={feed.frame} 
                              alt={`Camara de ${feed.user_name}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-600">
                              Sin imagen
                            </div>
                          )}
                          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 px-2 py-1 rounded">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            <span className="text-xs text-white">LIVE</span>
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="text-white font-medium">{feed.user_name}</p>
                          <p className="text-xs text-gray-500">
                            Ultima actualizacion: {new Date(feed.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Panel de Monitoreo existente */}
            <MonitoreoPanel users={users} />
          </div>
        )}


        {/* Contabilidad Tab */}
        {activeTab === "contabilidad" && (
          <div className="space-y-6">
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader>
                <CardTitle className="text-white">💵 Contabilidad Quincenal</CardTitle>
                <p className="text-sm text-gray-400 mt-2">
                  Reporte quincenal de rentabilidad de todas las modelos activas
                </p>
              </CardHeader>
              <CardContent>
                {/* Selector de Quincena */}
                <div className="mb-6 flex gap-4 items-end">
                  <div className="flex-1">
                    <Label className="text-gray-300">Fecha Inicio</Label>
                    <Input
                      type="date"
                      value={quincenaInicio}
                      onChange={(e) => setQuincenaInicio(e.target.value)}
                      className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-gray-300">Fecha Fin</Label>
                    <Input
                      type="date"
                      value={quincenaFin}
                      onChange={(e) => setQuincenaFin(e.target.value)}
                      className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    />
                  </div>
                  <Button
                    onClick={fetchReportesQuincenales}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    📊 Cargar Reporte
                  </Button>
                  <Button
                    onClick={() => setOpenHistorialContabilidadDialog(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    📁 Ver Historial
                  </Button>
                </div>

                {/* Tabla de Contabilidad */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-zinc-800">
                        <th className="border border-zinc-700 p-3 text-left text-white font-semibold">Nombre Modelo</th>
                        <th className="border border-zinc-700 p-3 text-center text-white font-semibold">Año Ingreso</th>
                        <th className="border border-zinc-700 p-3 text-center text-white font-semibold">% Agencia</th>
                        <th className="border border-zinc-700 p-3 text-right text-white font-semibold">Valor Neto</th>
                        <th className="border border-zinc-700 p-3 text-right text-white font-semibold">Valor Neto Modelo</th>
                        <th className="border border-zinc-700 p-3 text-right text-white font-semibold">Valor Neto Agencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportesQuincenales.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="border border-zinc-700 p-8 text-center text-gray-400">
                            No hay datos para este periodo. Ingresa los valores netos para cada modelo.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {reportesQuincenales.map((reporte) => {
                            const porcentajeModelo = 100 - reporte.porcentaje_agencia;
                            const valorNetoModelo = reporte.valor_neto * (porcentajeModelo / 100);
                            const valorNetoAgencia = reporte.valor_neto * (reporte.porcentaje_agencia / 100);
                            
                            return (
                              <tr key={reporte.modelo_id} className="hover:bg-zinc-800/50">
                                <td className="border border-zinc-700 p-3 text-white">{reporte.modelo_name}</td>
                                <td className="border border-zinc-700 p-3 text-center text-gray-300">{reporte.año_ingreso}</td>
                                <td className="border border-zinc-700 p-3 text-center">
                                  <Input
                                    type="number"
                                    step="1"
                                    min="0"
                                    max="100"
                                    value={reporte.porcentaje_agencia || ""}
                                    onChange={(e) => handleUpdateLocalReporte(reporte.modelo_id, 'porcentaje_agencia', e.target.value)}
                                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-purple-300 text-center w-20 mx-auto"
                                    placeholder="40"
                                  />
                                </td>
                                <td className="border border-zinc-700 p-3 text-right">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={reporte.valor_neto || ""}
                                    onChange={(e) => handleUpdateLocalReporte(reporte.modelo_id, 'valor_neto', e.target.value)}
                                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white text-right"
                                    placeholder="0.00"
                                  />
                                </td>
                                <td className="border border-zinc-700 p-3 text-right text-blue-300 font-semibold">
                                  ${valorNetoModelo.toFixed(2)}
                                </td>
                                <td className="border border-zinc-700 p-3 text-right text-green-400 font-semibold">
                                  ${valorNetoAgencia.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                          {/* TOTAL Row */}
                          <tr className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 font-bold">
                            <td className="border-2 border-green-500/50 p-3 text-white text-lg" colSpan="3">
                              TOTAL
                            </td>
                            <td className="border-2 border-green-500/50 p-3 text-right text-white text-lg">
                              ${reportesQuincenales.reduce((sum, r) => sum + (r.valor_neto || 0), 0).toFixed(2)}
                            </td>
                            <td className="border-2 border-green-500/50 p-3 text-right text-blue-400 text-lg">
                              ${reportesQuincenales.reduce((sum, r) => {
                                const porcentajeModelo = 100 - r.porcentaje_agencia;
                                return sum + (r.valor_neto * (porcentajeModelo / 100));
                              }, 0).toFixed(2)}
                            </td>
                            <td className="border-2 border-green-500/50 p-3 text-right text-green-400 text-lg">
                              ${reportesQuincenales.reduce((sum, r) => {
                                return sum + (r.valor_neto * (r.porcentaje_agencia / 100));
                              }, 0).toFixed(2)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Botones de Acción */}
                {reportesQuincenales.length > 0 && (
                  <div className="mt-6 flex justify-between items-center gap-4">
                    <Button
                      onClick={() => setOpenHistorialContabilidadDialog(true)}
                      className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3"
                    >
                      📁 Ver Historial de Periodos
                    </Button>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleGuardarReporteQuincenal}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-8 py-3"
                      >
                        💾 Guardar Reporte
                      </Button>
                      <Button
                        onClick={handleCerrarPeriodo}
                        className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold px-8 py-3"
                      >
                        🔒 Cerrar Periodo
                      </Button>
                    </div>
                  </div>
                )}

                {/* Resumen */}
                {resumenQuincenal && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-2 border-purple-500/30 rounded-xl">
                    <h3 className="text-white font-bold text-lg mb-3">📊 Resumen del Periodo</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <div className="text-gray-400 text-xs">Modelos Activas</div>
                        <div className="text-white font-bold text-xl">{resumenQuincenal.cantidad_modelos}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs">Total Generado</div>
                        <div className="text-white font-bold text-xl">${resumenQuincenal.total_valor_neto.toFixed(0)}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs">Total Modelos</div>
                        <div className="text-blue-400 font-bold text-xl">${resumenQuincenal.total_valor_neto_modelos.toFixed(0)}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs">Total Agencia</div>
                        <div className="text-green-400 font-bold text-xl">${resumenQuincenal.total_valor_neto_agencia.toFixed(0)}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs">Comisión Chatter (5%)</div>
                        <div className="text-yellow-400 font-bold text-xl">${resumenQuincenal.comision_chatter?.toFixed(2) || '0.00'}</div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dialog de Historial de Contabilidad */}
            <Dialog open={openHistorialContabilidadDialog} onOpenChange={setOpenHistorialContabilidadDialog}>
              <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl text-white max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white text-2xl">📁 Historial de Periodos Cerrados</DialogTitle>
                </DialogHeader>
                
                {historialPeriodos.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No hay periodos cerrados aún
                  </div>
                ) : (
                  <div className="space-y-4">
                    {historialPeriodos.map((periodo, index) => (
                      <Card key={index} className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl">
                        <CardHeader>
                          <div className="flex justify-between items-center">
                            <div>
                              <CardTitle className="text-white">
                                Periodo: {periodo.quincena_inicio} a {periodo.quincena_fin}
                              </CardTitle>
                              <p className="text-sm text-gray-400 mt-1">
                                Cerrado: {new Date(periodo.fecha_cierre).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => setPeriodoSeleccionado(periodoSeleccionado?.quincena_inicio === periodo.quincena_inicio ? null : periodo)}
                                variant="outline"
                                className="border-zinc-600 text-white hover:bg-zinc-700"
                              >
                                {periodoSeleccionado?.quincena_inicio === periodo.quincena_inicio ? "Ocultar Detalle" : "Ver Detalle"}
                              </Button>
                              <Button
                                onClick={() => handleDeletePeriodo(periodo)}
                                variant="destructive"
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                🗑️ Eliminar Periodo
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {/* Resumen del Periodo */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div className="bg-zinc-900 p-3 rounded-lg">
                              <div className="text-gray-400 text-xs">Modelos</div>
                              <div className="text-white font-bold text-lg">{periodo.cantidad_modelos}</div>
                            </div>
                            <div className="bg-zinc-900 p-3 rounded-lg">
                              <div className="text-gray-400 text-xs">Total Generado</div>
                              <div className="text-white font-bold text-lg">${periodo.total_valor_neto.toFixed(2)}</div>
                            </div>
                            <div className="bg-zinc-900 p-3 rounded-lg">
                              <div className="text-gray-400 text-xs">Total Modelos</div>
                              <div className="text-blue-400 font-bold text-lg">${periodo.total_valor_neto_modelos.toFixed(2)}</div>
                            </div>
                            <div className="bg-zinc-900 p-3 rounded-lg">
                              <div className="text-gray-400 text-xs">Total Agencia</div>
                              <div className="text-green-400 font-bold text-lg">${periodo.total_valor_neto_agencia.toFixed(2)}</div>
                            </div>
                          </div>

                          {/* Detalle expandible */}
                          {periodoSeleccionado?.quincena_inicio === periodo.quincena_inicio && (
                            <div className="mt-4">
                              <h4 className="text-white font-semibold mb-3">Detalle por Modelo:</h4>
                              <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                  <thead>
                                    <tr className="bg-zinc-900">
                                      <th className="border border-zinc-700 p-2 text-left text-white text-sm">Modelo</th>
                                      <th className="border border-zinc-700 p-2 text-center text-white text-sm">% Agencia</th>
                                      <th className="border border-zinc-700 p-2 text-right text-white text-sm">Valor Neto</th>
                                      <th className="border border-zinc-700 p-2 text-right text-white text-sm">Valor Modelo</th>
                                      <th className="border border-zinc-700 p-2 text-right text-white text-sm">Valor Agencia</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {periodo.reportes.map((reporte) => (
                                      <tr key={reporte.id} className="hover:bg-zinc-700/50">
                                        <td className="border border-zinc-700 p-2 text-white text-sm">{reporte.modelo_name}</td>
                                        <td className="border border-zinc-700 p-2 text-center text-purple-300 text-sm">{reporte.porcentaje_agencia}%</td>
                                        <td className="border border-zinc-700 p-2 text-right text-white text-sm">${reporte.valor_neto.toFixed(2)}</td>
                                        <td className="border border-zinc-700 p-2 text-right text-blue-300 text-sm">${reporte.valor_neto_modelo.toFixed(2)}</td>
                                        <td className="border border-zinc-700 p-2 text-right text-green-400 text-sm">${reporte.valor_neto_agencia.toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Agenda Tab */}
        {activeTab === "agenda" && (
          <div className="space-y-6">
            {/* Header with Actions */}
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Gestión de Agenda Semanal</CardTitle>
                  <p className="text-sm text-gray-400 mt-2">
                    Administra las tareas semanales de la asistente. Puedes crear tareas para cada día de la semana.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Dialog open={openTaskDialog} onOpenChange={setOpenTaskDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-purple-600 hover:bg-purple-700">
                        ➕ Nueva Tarea
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 text-white border-zinc-800">
                      <DialogHeader>
                        <DialogTitle>Crear Nueva Tarea</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Título de la tarea</Label>
                          <Input
                            value={newTask.title}
                            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                            placeholder="Ej: Revisar emails"
                            className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                          />
                        </div>
                        <div>
                          <Label>Día de la semana</Label>
                          <Select value={newTask.day} onValueChange={(val) => setNewTask({ ...newTask, day: val })}>
                            <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                              <SelectItem value="lunes">Lunes</SelectItem>
                              <SelectItem value="martes">Martes</SelectItem>
                              <SelectItem value="miercoles">Miércoles</SelectItem>
                              <SelectItem value="jueves">Jueves</SelectItem>
                              <SelectItem value="viernes">Viernes</SelectItem>
                              <SelectItem value="sabado">Sábado</SelectItem>
                              <SelectItem value="domingo">Domingo</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Tipo de tarea</Label>
                          <Select value={newTask.task_type} onValueChange={(val) => setNewTask({ ...newTask, task_type: val })}>
                            <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                              <SelectItem value="fija">Tarea Fija</SelectItem>
                              <SelectItem value="especial">Tarea Especial</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Prioridad</Label>
                          <Select value={newTask.priority} onValueChange={(val) => setNewTask({ ...newTask, priority: val })}>
                            <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="importante">Importante</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleCreateTask} className="w-full bg-purple-600 hover:bg-purple-700">
                          Crear Tarea
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  <Button onClick={handleDuplicateWeek} className="bg-blue-600 hover:bg-blue-700">
                    📋 Duplicar a Próxima Semana
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                  <p className="text-purple-200 text-sm">
                    💡 <strong>Tip:</strong> La asistente puede ver y marcar estas tareas como completadas desde su panel.
                    Usuario: <code className="bg-black/30 px-2 py-1 rounded">asistente1</code> / Contraseña: <code className="bg-black/30 px-2 py-1 rounded">Asist2024!</code>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Tasks by Day */}
            {["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"].map(day => {
              const dayTasks = weekTasks.filter(t => t.day === day);
              const dayLabels = {
                "lunes": "Lunes",
                "martes": "Martes", 
                "miercoles": "Miércoles",
                "jueves": "Jueves",
                "viernes": "Viernes",
                "sabado": "Sábado",
                "domingo": "Domingo"
              };
              
              return (
                <Card key={day} className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center justify-between">
                      <span>{dayLabels[day]}</span>
                      <span className="text-sm text-gray-400">{dayTasks.length} tareas</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dayTasks.length === 0 ? (
                      <div className="text-center text-gray-400 py-4">
                        No hay tareas para este día
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {dayTasks.map(task => (
                          <div
                            key={task.id}
                            className={`p-3 rounded-lg border-2 flex items-center justify-between ${
                              task.priority === "importante"
                                ? "bg-red-900/20 border-red-500/50"
                                : task.task_type === "especial"
                                ? "bg-purple-900/20 border-purple-500/50"
                                : "bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl"
                            }`}
                          >
                            <div className="flex-1">
                              <div className="text-white font-medium">{task.title}</div>
                              <div className="flex gap-2 mt-1">
                                {task.task_type === "fija" && (
                                  <span className="text-xs px-2 py-1 rounded bg-orange-900/30 text-orange-300 border border-orange-500/30">
                                    Tarea fija
                                  </span>
                                )}
                                {task.task_type === "especial" && (
                                  <span className="text-xs px-2 py-1 rounded bg-purple-900/30 text-purple-300 border border-purple-500/30">
                                    Tarea Especial
                                  </span>
                                )}
                                {task.priority === "importante" && (
                                  <span className="text-xs px-2 py-1 rounded bg-red-900/30 text-red-300 border border-red-500/30">
                                    IMPORTANTE
                                  </span>
                                )}
                                {task.completed && (
                                  <span className="text-xs px-2 py-1 rounded bg-green-900/30 text-green-300 border border-green-500/30">
                                    ✓ Completada
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button
                              onClick={() => handleDeleteTask(task.id)}
                              variant="outline"
                              size="sm"
                              className="ml-4 border-red-500/50 text-red-400 hover:bg-red-900/20"
                            >
                              🗑️ Eliminar
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === "applications" && (
          <div className="space-y-6">
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">Aplicaciones de Modelos</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant={applicationFilter === "pendiente" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setApplicationFilter("pendiente")}
                      className="bg-yellow-600 hover:bg-yellow-700"
                    >
                      Pendientes ({modelApplications.filter(app => app.status === "pendiente").length})
                    </Button>
                    <Button
                      variant={applicationFilter === "aprobada" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setApplicationFilter("aprobada")}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Aprobadas
                    </Button>
                    <Button
                      variant={applicationFilter === "rechazada" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setApplicationFilter("rechazada")}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Rechazadas
                    </Button>
                    <Button
                      variant={applicationFilter === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setApplicationFilter("all")}
                    >
                      Todas
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {modelApplications.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No hay aplicaciones {applicationFilter !== "all" ? `${applicationFilter}s` : ""}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {modelApplications.map((app) => (
                      <Card key={app.id} className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-xl font-bold text-white">{app.nombre}</h3>
                              <p className="text-sm text-gray-400">
                                {new Date(app.created_at).toLocaleDateString('es-ES', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              app.status === 'pendiente' ? 'bg-yellow-600 text-white' :
                              app.status === 'aprobada' ? 'bg-green-600 text-white' :
                              'bg-red-600 text-white'
                            }`}>
                              {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-gray-400">Email:</p>
                              <p className="text-white">{app.email}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-400">WhatsApp:</p>
                              <p className="text-white">{app.whatsapp}</p>
                            </div>
                            {app.instagram && (
                              <div>
                                <p className="text-sm text-gray-400">Instagram:</p>
                                <p className="text-white">{app.instagram}</p>
                              </div>
                            )}
                            {app.onlyfans && (
                              <div>
                                <p className="text-sm text-gray-400">OnlyFans:</p>
                                <p className="text-white">{app.onlyfans}</p>
                              </div>
                            )}
                            {app.edad && (
                              <div>
                                <p className="text-sm text-gray-400">Edad:</p>
                                <p className="text-white">{app.edad} años</p>
                              </div>
                            )}
                            {app.ciudad && (
                              <div>
                                <p className="text-sm text-gray-400">Ciudad:</p>
                                <p className="text-white">{app.ciudad}</p>
                              </div>
                            )}
                          </div>

                          <div className="mb-4">
                            <p className="text-sm text-gray-400 mb-2">Descripción:</p>
                            <p className="text-white bg-zinc-900 p-3 rounded">{app.descripcion}</p>
                          </div>

                          <div className="flex gap-2 justify-end">
                            {app.status === "pendiente" && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => updateApplicationStatus(app.id, "aprobada")}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  Aprobar
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => updateApplicationStatus(app.id, "rechazada")}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Rechazar
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteApplication(app.id)}
                              className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                            >
                              Eliminar
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {/* Nómina / Payslips Tab */}
        {activeTab === "nomina" && (
          <div className="space-y-6">
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">💰 Colillas de Pago</CardTitle>
                <Button 
                  onClick={() => setOpenPayslipDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  + Crear Colilla
                </Button>
              </CardHeader>
              <CardContent>
                {payslips.length === 0 ? (
                  <div className="text-center text-gray-400 py-16">
                    <p>No hay colillas de pago registradas</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {payslips.map((payslip) => (
                      <Card key={payslip.id} className={`bg-[#0a0a0a] border ${payslip.pagado ? 'border-green-500/40' : 'border-white/[0.08]'} shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl`}>
                        <CardContent className="p-4">
                          {/* Header */}
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h3 className="text-white font-bold text-lg">{payslip.user_name}</h3>
                                {payslip.pagado ? (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30 font-semibold" data-testid={`payslip-status-paid-${payslip.id}`}>
                                    ✓ Pagada
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 font-semibold" data-testid={`payslip-status-pending-${payslip.id}`}>
                                    ⏳ Pendiente
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-400">{payslip.periodo}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(payslip.fecha_inicio).toLocaleDateString('es')} - {new Date(payslip.fecha_fin).toLocaleDateString('es')}
                              </p>
                              {payslip.pagado && payslip.fecha_pago && (
                                <p className="text-xs text-green-400 mt-1">
                                  Pagada el {new Date(payslip.fecha_pago).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                if (window.confirm('¿Eliminar esta colilla?')) {
                                  handleDeletePayslip(payslip.id);
                                }
                              }}
                              className="text-red-500 hover:text-red-400"
                              data-testid={`delete-payslip-btn-${payslip.id}`}
                            >
                              🗑️
                            </button>
                          </div>

                          {/* Detalles */}
                          <div className="space-y-2 border-t border-zinc-700 pt-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Salario Base (Quincenal):</span>
                              <span className="text-white font-medium">${payslip.salario_base.toLocaleString()} COP</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Días de la Quincena:</span>
                              <span className="text-white">{payslip.dias_quincena} días</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Valor por Día:</span>
                              <span className="text-white">${payslip.valor_dia.toLocaleString()} COP</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Días Trabajados:</span>
                              <span className="text-white font-semibold">{payslip.dias_trabajados} días</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Horas Trabajadas:</span>
                              <span className="text-white">{payslip.horas_trabajadas}h</span>
                            </div>
                            
                            <div className="flex justify-between text-sm bg-zinc-900 p-2 rounded">
                              <span className="text-gray-300">Salario Devengado:</span>
                              <span className="text-blue-400 font-semibold">${payslip.salario_devengado.toLocaleString()} COP</span>
                            </div>
                            
                            {payslip.bonificaciones > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-400">Bonificaciones:</span>
                                <span className="text-green-400">+${payslip.bonificaciones.toLocaleString()} COP</span>
                              </div>
                            )}
                            
                            {payslip.deducciones > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-400">Deducciones:</span>
                                <span className="text-red-400">-${payslip.deducciones.toLocaleString()} COP</span>
                              </div>
                            )}
                            
                            {/* Total */}
                            <div className="flex justify-between text-lg font-bold border-t border-zinc-700 pt-2 mt-2">
                              <span className="text-white">Total Pagado:</span>
                              <span className="text-green-500">${payslip.total_pagado.toLocaleString()} COP</span>
                            </div>
                            
                            {payslip.notas && (
                              <div className="mt-3 p-2 bg-zinc-900 rounded text-xs">
                                <p className="text-gray-400">Notas:</p>
                                <p className="text-gray-300">{payslip.notas}</p>
                              </div>
                            )}

                            {/* Observaciones (post-creación, ej: deudas) */}
                            {payslip.observaciones && (
                              <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs" data-testid={`payslip-obs-${payslip.id}`}>
                                <p className="text-amber-400 font-semibold mb-1">📝 Observación:</p>
                                <p className="text-amber-200 whitespace-pre-wrap">{payslip.observaciones}</p>
                              </div>
                            )}

                            {/* Acciones: Marcar pagada + Observaciones */}
                            <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-800">
                              <Button
                                size="sm"
                                onClick={() => handleTogglePayslipPaid(payslip)}
                                className={`flex-1 text-xs ${payslip.pagado ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-green-600 hover:bg-green-700'}`}
                                data-testid={`toggle-paid-btn-${payslip.id}`}
                              >
                                {payslip.pagado ? '↩️ Marcar pendiente' : '✓ Marcar pagada'}
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleEditPayslipObservaciones(payslip)}
                                className="flex-1 bg-amber-600 hover:bg-amber-700 text-xs"
                                data-testid={`edit-obs-btn-${payslip.id}`}
                              >
                                📝 {payslip.observaciones ? 'Editar' : 'Agregar'} obs.
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create Payslip Dialog */}
        <Dialog open={openPayslipDialog} onOpenChange={(open) => {
          setOpenPayslipDialog(open);
          if (!open) {
            setPayslipWorkDetail(null);
          }
        }}>
          <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Crear Colilla de Pago</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Empleado</Label>
                <Select
                  value={newPayslip.user_id}
                  onValueChange={handlePayslipUserChange}
                >
                  <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                    <SelectValue placeholder="Selecciona un empleado" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                    {users.filter(u => u.role !== 'administrativo').map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-300">Período</Label>
                <Input
                  value={newPayslip.periodo}
                  onChange={(e) => setNewPayslip({ ...newPayslip, periodo: e.target.value })}
                  className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  placeholder="Ej: Noviembre 2025 o Quincena 1 - Nov 2025"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={newPayslip.fecha_inicio}
                    onChange={(e) => handlePayslipDateChange('fecha_inicio', e.target.value)}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Fecha Fin</Label>
                  <Input
                    type="date"
                    value={newPayslip.fecha_fin}
                    onChange={(e) => handlePayslipDateChange('fecha_fin', e.target.value)}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  />
                </div>
              </div>

              {/* Auto-calculated work data indicator */}
              {calculatingPayslip && (
                <div className="p-3 bg-blue-900/30 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-400 text-sm flex items-center gap-2">
                    <span className="animate-spin">⏳</span> Calculando días y horas trabajadas...
                  </p>
                </div>
              )}

              {payslipWorkDetail && !calculatingPayslip && (
                <div className="p-3 bg-green-900/30 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-sm font-medium mb-2">
                    ✅ Datos calculados automáticamente
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-300">
                    <div>Días con ≥2h: <span className="text-white font-bold">{payslipWorkDetail.dias_trabajados}</span></div>
                    <div>Total horas: <span className="text-white font-bold">{payslipWorkDetail.horas_trabajadas}h</span></div>
                  </div>
                  {payslipWorkDetail.detalle_dias && payslipWorkDetail.detalle_dias.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-white">
                        Ver detalle por día ({payslipWorkDetail.detalle_dias.length} días con actividad)
                      </summary>
                      <div className="mt-2 max-h-32 overflow-y-auto text-xs space-y-1">
                        {payslipWorkDetail.detalle_dias.map((d, i) => (
                          <div key={i} className={`flex justify-between px-2 py-1 rounded ${d.horas >= 2 ? 'bg-green-900/20' : 'bg-red-900/20'}`}>
                            <span>{new Date(d.fecha + 'T12:00:00').toLocaleDateString('es')}</span>
                            <span className={d.horas >= 2 ? 'text-green-400' : 'text-red-400'}>{d.horas}h</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              <div>
                <Label className="text-gray-300">Salario Base (COP)</Label>
                <Input
                  type="number"
                  value={newPayslip.salario_base}
                  onChange={(e) => setNewPayslip({ ...newPayslip, salario_base: parseFloat(e.target.value) || 0 })}
                  className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  placeholder="1500000"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Días de la Quincena</Label>
                  <Input
                    type="number"
                    value={newPayslip.dias_quincena}
                    onChange={(e) => setNewPayslip({ ...newPayslip, dias_quincena: parseInt(e.target.value) || 15 })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    placeholder="15"
                  />
                  <p className="text-xs text-gray-500 mt-1">Normalmente 15 días</p>
                </div>
                <div>
                  <Label className="text-gray-300">Días Trabajados</Label>
                  <Input
                    type="number"
                    value={newPayslip.dias_trabajados}
                    onChange={(e) => setNewPayslip({ ...newPayslip, dias_trabajados: parseInt(e.target.value) || 0 })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    placeholder="14"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Horas Trabajadas</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newPayslip.horas_trabajadas}
                  onChange={(e) => setNewPayslip({ ...newPayslip, horas_trabajadas: parseFloat(e.target.value) || 0 })}
                  className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  placeholder="112"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Bonificaciones ($)</Label>
                  <Input
                    type="number"
                    value={newPayslip.bonificaciones}
                    onChange={(e) => setNewPayslip({ ...newPayslip, bonificaciones: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Deducciones ($)</Label>
                  <Input
                    type="number"
                    value={newPayslip.deducciones}
                    onChange={(e) => setNewPayslip({ ...newPayslip, deducciones: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Notas (Opcional)</Label>
                <textarea
                  value={newPayslip.notas}
                  onChange={(e) => setNewPayslip({ ...newPayslip, notas: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white rounded p-2"
                  rows="3"
                  placeholder="Notas adicionales..."
                />
              </div>

              {/* Vista previa del total */}
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <div className="text-center">
                  <p className="text-gray-300 text-sm mb-1">Total a Pagar (estimado)</p>
                  <p className="text-3xl font-bold text-green-500">
                    ${(() => {
                      const valorDia = newPayslip.dias_quincena > 0 
                        ? newPayslip.salario_base / newPayslip.dias_quincena 
                        : 0;
                      const salarioDevengado = valorDia * newPayslip.dias_trabajados;
                      return (salarioDevengado + newPayslip.bonificaciones - newPayslip.deducciones).toLocaleString();
                    })()}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Valor día: ${newPayslip.dias_quincena > 0 ? Math.round(newPayslip.salario_base / newPayslip.dias_quincena).toLocaleString() : 0} × {newPayslip.dias_trabajados} días
                  </p>
                </div>
              </div>

              <Button
                onClick={handleCreatePayslip}
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={calculatingPayslip}
              >
                {calculatingPayslip ? 'Calculando...' : 'Crear Colilla'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reportes Tab - CEO Daily Reports */}
        {activeTab === "reportes" && (
          <div className="space-y-6">
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Todos los Reportes Diarios</CardTitle>
                  <p className="text-xs text-gray-400 mt-1">Incluye reportes del CEO/Admin y de los chatters. Solo puedes editar/eliminar tus propios reportes.</p>
                </div>
                <Dialog open={openCeoReportDialog} onOpenChange={setOpenCeoReportDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      + Nuevo Reporte
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
                    <DialogHeader>
                      <DialogTitle>Crear Reporte Diario</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-gray-300">Fecha</Label>
                        <Input
                          type="date"
                          value={newCeoReport.date}
                          onChange={(e) => setNewCeoReport({ ...newCeoReport, date: e.target.value })}
                          className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                        />
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-rose-300">Subs ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newCeoReport.subs_amount}
                            onChange={(e) => setNewCeoReport({ ...newCeoReport, subs_amount: parseFloat(e.target.value) || 0 })}
                            className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label className="text-emerald-300">Messages ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newCeoReport.messages_amount}
                            onChange={(e) => setNewCeoReport({ ...newCeoReport, messages_amount: parseFloat(e.target.value) || 0 })}
                            className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label className="text-blue-300">Tips ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newCeoReport.tips_amount}
                            onChange={(e) => setNewCeoReport({ ...newCeoReport, tips_amount: parseFloat(e.target.value) || 0 })}
                            className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                            placeholder="0"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-300">Total</Label>
                        <div className="text-2xl font-bold text-amber-400">
                          ${((newCeoReport.subs_amount || 0) + (newCeoReport.messages_amount || 0) + (newCeoReport.tips_amount || 0)).toLocaleString()}
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-300">Notas (opcional)</Label>
                        <Input
                          value={newCeoReport.notes}
                          onChange={(e) => setNewCeoReport({ ...newCeoReport, notes: e.target.value })}
                          className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                          placeholder="Notas adicionales..."
                        />
                      </div>

                      <Button onClick={handleCreateCeoReport} className="w-full bg-blue-600 hover:bg-blue-700">
                        Guardar Reporte
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {ceoReports.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay reportes. Crea tu primer reporte diario.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ceoReports.map((report) => {
                      const isChatter = report._source === "chatter";
                      // Calcular total efectivo
                      const sbmSum = Object.values(report.sales_by_model || {})
                        .reduce((s, v) => s + (parseFloat(v) || 0), 0);
                      const totalUsd = isChatter
                        ? Math.max(parseFloat(report.money_collected) || 0, sbmSum)
                        : (parseFloat(report.subs_amount) || 0) + (parseFloat(report.messages_amount) || 0) + (parseFloat(report.tips_amount) || 0);
                      const mymSbm = Object.values(report.mym_sales_by_model || {})
                        .reduce((s, v) => s + (parseFloat(v) || 0), 0);
                      const totalMym = Math.max(parseFloat(report.mym_money_eur) || 0, mymSbm);
                      const rowKey = `${report._source}-${report.id}`;
                      return (
                      <div
                        key={rowKey}
                        data-testid={`report-row-${report.id}`}
                        className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_2px_8px_rgba(0,0,0,0.4)] rounded-lg p-4 flex flex-wrap items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-4 min-w-0 flex-wrap">
                          <div className="text-center">
                            <div className="text-lg font-bold text-white">
                              {new Date(report.date).getDate()}
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(report.date).toLocaleDateString('es', { month: 'short' })}
                            </div>
                          </div>
                          <div className="h-10 w-px bg-zinc-700"></div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                                isChatter
                                  ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                                  : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                              }`}
                            >
                              {isChatter ? "Chatter" : "CEO/Admin"}
                            </span>
                            {totalMym > 0 && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-300 border border-pink-500/30 font-semibold">
                                MYM
                              </span>
                            )}
                            <span className="text-sm text-gray-200 max-w-[180px] truncate">
                              {report.user_name || "—"}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-6">
                            <div>
                              <div className="text-xs text-rose-400">Subs</div>
                              <div className="font-semibold text-white">${(report.subs_amount || 0).toLocaleString()}</div>
                            </div>
                            <div>
                              <div className="text-xs text-emerald-400">Messages</div>
                              <div className="font-semibold text-white">${(report.messages_amount || 0).toLocaleString()}</div>
                            </div>
                            <div>
                              <div className="text-xs text-blue-400">Tips</div>
                              <div className="font-semibold text-white">${(report.tips_amount || 0).toLocaleString()}</div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="text-xs text-gray-400">Total</div>
                            <div className="text-xl font-bold text-amber-400">
                              ${totalUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </div>
                            {totalMym > 0 && (
                              <div className="text-xs text-pink-300 font-semibold">
                                + €{totalMym.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                              </div>
                            )}
                          </div>
                          {!isChatter && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditCeoReport(report)}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                              >
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCeoReport(report.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                              >
                                Eliminar
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dialog para editar reporte */}
            <Dialog open={!!editingCeoReport} onOpenChange={(open) => !open && setEditingCeoReport(null)}>
              <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle>Editar Reporte - {editingCeoReport?.date}</DialogTitle>
                </DialogHeader>
                {editingCeoReport && (
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-fuchsia-300">Subs ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingCeoReport.subs_amount}
                          onChange={(e) => setEditingCeoReport({ ...editingCeoReport, subs_amount: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-cyan-300">Messages ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingCeoReport.messages_amount}
                          onChange={(e) => setEditingCeoReport({ ...editingCeoReport, messages_amount: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-violet-300">Tips ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingCeoReport.tips_amount}
                          onChange={(e) => setEditingCeoReport({ ...editingCeoReport, tips_amount: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-300">Total</Label>
                      <div className="text-2xl font-bold text-amber-400">
                        ${((editingCeoReport.subs_amount || 0) + (editingCeoReport.messages_amount || 0) + (editingCeoReport.tips_amount || 0)).toLocaleString()}
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-300">Notas</Label>
                      <Input
                        value={editingCeoReport.notes || ''}
                        onChange={(e) => setEditingCeoReport({ ...editingCeoReport, notes: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>

                    <Button onClick={handleUpdateCeoReport} className="w-full bg-blue-600 hover:bg-blue-700">
                      Guardar Cambios
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* CRM Tab - Usuarios Potenciales */}
        {activeTab === "crm" && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {crmStats && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl p-4">
                  <p className="text-gray-400 text-sm">Total Usuarios</p>
                  <p className="text-3xl font-bold text-white">{crmStats.total}</p>
                </Card>
                <Card className="bg-[#0a0a0a] border border-purple-500/20 shadow-[0_0_0_1px_rgba(168,85,247,0.1)] rounded-2xl p-4">
                  <p className="text-purple-400 text-sm">💰 Gasto Total</p>
                  <p className="text-3xl font-bold text-purple-400">${(crmStats.total_gasto || 0).toLocaleString()}</p>
                </Card>
                <Card className="bg-[#0a0a0a] border border-green-500/20 shadow-[0_0_0_1px_rgba(34,197,94,0.1)] rounded-2xl p-4">
                  <p className="text-green-400 text-sm">High Spenders</p>
                  <p className="text-3xl font-bold text-green-400">{crmStats.by_spending_level?.high || 0}</p>
                </Card>
                <Card className="bg-[#0a0a0a] border border-yellow-500/20 shadow-[0_0_0_1px_rgba(234,179,8,0.1)] rounded-2xl p-4">
                  <p className="text-yellow-400 text-sm">Medium Spenders</p>
                  <p className="text-3xl font-bold text-yellow-400">{crmStats.by_spending_level?.medium || 0}</p>
                </Card>
                <Card className="bg-[#0a0a0a] border border-gray-500/20 shadow-[0_0_0_1px_rgba(156,163,175,0.1)] rounded-2xl p-4">
                  <p className="text-gray-400 text-sm">Low Spenders</p>
                  <p className="text-3xl font-bold text-gray-300">{crmStats.by_spending_level?.low || 0}</p>
                </Card>
                <Card
                  className={`bg-[#0a0a0a] border rounded-2xl p-4 cursor-pointer transition-all ${
                    crmFilterCashback
                      ? 'border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.4)]'
                      : 'border-red-500/20 shadow-[0_0_0_1px_rgba(239,68,68,0.1)] hover:border-red-500/50'
                  }`}
                  onClick={() => setCrmFilterCashback((v) => !v)}
                  data-testid="cashback-stat-card"
                >
                  <p className="text-red-400 text-sm">🚨 Cashbacks</p>
                  <p className="text-3xl font-bold text-red-400">{crmStats.cashback_count || 0}</p>
                  <p className="text-xs text-red-300/70 mt-1">
                    ${(crmStats.cashback_total_monto || 0).toLocaleString()}
                  </p>
                </Card>
              </div>
            )}

            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <CardTitle className="text-white">Base de Datos de Usuarios</CardTitle>
                <Dialog open={openCrmDialog} onOpenChange={setOpenCrmDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">+ Nuevo Usuario</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Agregar Usuario al CRM</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                      <div>
                        <Label className="text-gray-300">Username *</Label>
                        <Input
                          value={newCrmUser.username}
                          onChange={(e) => setNewCrmUser({ ...newCrmUser, username: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="@usuario_plataforma"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Modelo Asociada *</Label>
                        <Select value={newCrmUser.modelo_id} onValueChange={(v) => setNewCrmUser({ ...newCrmUser, modelo_id: v })}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                            <SelectValue placeholder="Seleccionar modelo" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {models.map((m) => (
                              <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">Nivel de Gasto</Label>
                        <Select value={newCrmUser.nivel_gasto} onValueChange={(v) => setNewCrmUser({ ...newCrmUser, nivel_gasto: v })}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="low" className="text-gray-300">Low Spender</SelectItem>
                            <SelectItem value="medium" className="text-yellow-400">Medium Spender</SelectItem>
                            <SelectItem value="high" className="text-green-400">High Spender</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">💰 Gasto Total ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newCrmUser.gasto_total}
                          onChange={(e) => setNewCrmUser({ ...newCrmUser, gasto_total: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Origen / País</Label>
                        <Input
                          value={newCrmUser.origen}
                          onChange={(e) => setNewCrmUser({ ...newCrmUser, origen: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="Ej: USA, Colombia, España..."
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Gustos / Preferencias</Label>
                        <Input
                          value={newCrmUser.gustos}
                          onChange={(e) => setNewCrmUser({ ...newCrmUser, gustos: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="Preferencias del usuario..."
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Solicitudes Frecuentes</Label>
                        <Input
                          value={newCrmUser.solicitudes}
                          onChange={(e) => setNewCrmUser({ ...newCrmUser, solicitudes: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="Qué suele pedir..."
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Notas Adicionales</Label>
                        <Input
                          value={newCrmUser.notas}
                          onChange={(e) => setNewCrmUser({ ...newCrmUser, notas: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="Información adicional..."
                        />
                      </div>

                      {/* ============ CASHBACK SECTION (CREATE) ============ */}
                      <div className="border-t border-zinc-700 pt-4 mt-2 bg-red-500/5 -mx-2 px-4 py-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-red-400 text-lg">⚠️</span>
                          <h4 className="text-white font-semibold text-sm">Registro de Cashback (Posible Estafador)</h4>
                        </div>
                        <div className="flex items-center gap-3 mb-3">
                          <input
                            id="cashback-toggle-create"
                            type="checkbox"
                            checked={!!newCrmUser.hizo_cashback}
                            onChange={(e) => setNewCrmUser({ ...newCrmUser, hizo_cashback: e.target.checked })}
                            className="w-5 h-5 rounded accent-red-600"
                            data-testid="cashback-toggle-create"
                          />
                          <label htmlFor="cashback-toggle-create" className="text-gray-300 text-sm cursor-pointer">
                            Este usuario realizó un cashback / chargeback
                          </label>
                        </div>
                        {newCrmUser.hizo_cashback && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-gray-300 text-xs">Fecha</Label>
                                <Input
                                  type="date"
                                  value={newCrmUser.fecha_cashback || ''}
                                  onChange={(e) => setNewCrmUser({ ...newCrmUser, fecha_cashback: e.target.value })}
                                  className="bg-zinc-800 border-zinc-700 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-300 text-xs">Monto ($)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={newCrmUser.monto_cashback || 0}
                                  onChange={(e) => setNewCrmUser({ ...newCrmUser, monto_cashback: parseFloat(e.target.value) || 0 })}
                                  className="bg-zinc-800 border-zinc-700 text-white"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-gray-300 text-xs">Notas del cashback</Label>
                              <Input
                                value={newCrmUser.notas_cashback || ''}
                                onChange={(e) => setNewCrmUser({ ...newCrmUser, notas_cashback: e.target.value })}
                                placeholder="Razón, plataforma, evidencia..."
                                className="bg-zinc-800 border-zinc-700 text-white"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <Button onClick={handleCreateCrmUser} className="w-full bg-blue-600 hover:bg-blue-700">
                        Guardar Usuario
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {/* Filtros y Búsqueda */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <Input
                      placeholder="Buscar por username..."
                      value={crmSearchTerm}
                      onChange={(e) => setCrmSearchTerm(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <Select value={crmFilterGasto} onValueChange={setCrmFilterGasto}>
                    <SelectTrigger className="w-full md:w-48 bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Nivel de gasto" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="all" className="text-white">Todos los niveles</SelectItem>
                      <SelectItem value="high" className="text-green-400">High Spender</SelectItem>
                      <SelectItem value="medium" className="text-yellow-400">Medium Spender</SelectItem>
                      <SelectItem value="low" className="text-gray-300">Low Spender</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={crmFilterModelo} onValueChange={setCrmFilterModelo}>
                    <SelectTrigger className="w-full md:w-48 bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Modelo" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="all" className="text-white">Todas las modelos</SelectItem>
                      {models.map((m) => (
                        <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => setCrmFilterCashback((v) => !v)}
                    data-testid="cashback-filter-toggle"
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all whitespace-nowrap ${
                      crmFilterCashback
                        ? 'bg-red-500/20 border-red-500 text-red-400'
                        : 'bg-zinc-800 border-zinc-700 text-gray-400 hover:border-red-500/50'
                    }`}
                  >
                    🚨 Solo Cashback
                  </button>
                </div>

                {/* Tabla de Usuarios */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Username</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Modelo</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Nivel</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">💰 Gasto Total</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Origen</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crmUsers.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-8 text-gray-500">
                            No hay usuarios en el CRM. ¡Agrega el primero!
                          </td>
                        </tr>
                      ) : (
                        crmUsers.map((crmUser) => (
                          <tr
                            key={crmUser.id}
                            className={`border-b border-white/5 hover:bg-white/5 ${
                              crmUser.hizo_cashback ? 'bg-red-500/5' : ''
                            }`}
                          >
                            <td className="py-3 px-2 text-white font-medium">
                              <div className="flex items-center gap-2">
                                <span>{crmUser.username}</span>
                                {crmUser.hizo_cashback && (
                                  <span
                                    className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/40"
                                    data-testid={`cashback-badge-${crmUser.id}`}
                                    title={crmUser.notas_cashback || 'Usuario marcado por cashback'}
                                  >
                                    🚨 ESTAFADOR
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-gray-300">{crmUser.modelo_name}</td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                crmUser.nivel_gasto === 'high' ? 'bg-green-500/20 text-green-400' :
                                crmUser.nivel_gasto === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {crmUser.nivel_gasto === 'high' ? 'High' : crmUser.nivel_gasto === 'medium' ? 'Medium' : 'Low'}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-purple-400 font-semibold">${(crmUser.gasto_total || 0).toLocaleString()}</td>
                            <td className="py-3 px-2 text-gray-300">{crmUser.origen || '-'}</td>
                            <td className="py-3 px-2">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                                  onClick={() => setEditingCrmUser(crmUser)}
                                >
                                  Ver/Editar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                                  onClick={() => handleDeleteCrmUser(crmUser.id)}
                                >
                                  Eliminar
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Dialog para editar usuario CRM */}
            <Dialog open={!!editingCrmUser} onOpenChange={() => setEditingCrmUser(null)}>
              <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    👤 {editingCrmUser?.username}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      editingCrmUser?.nivel_gasto === 'high' ? 'bg-green-500/20 text-green-400' :
                      editingCrmUser?.nivel_gasto === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {editingCrmUser?.nivel_gasto === 'high' ? 'High' : editingCrmUser?.nivel_gasto === 'medium' ? 'Medium' : 'Low'} Spender
                    </span>
                  </DialogTitle>
                </DialogHeader>
                {editingCrmUser && (
                  <div className="space-y-4 py-2 max-h-[80vh] overflow-y-auto pr-2">
                    <div>
                      <Label className="text-gray-300">Username</Label>
                      <Input
                        value={editingCrmUser.username}
                        onChange={(e) => setEditingCrmUser({ ...editingCrmUser, username: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Modelo Asociada</Label>
                        <Select value={editingCrmUser.modelo_id} onValueChange={(v) => setEditingCrmUser({ ...editingCrmUser, modelo_id: v })}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {models.map((m) => (
                              <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">Nivel de Gasto</Label>
                        <Select value={editingCrmUser.nivel_gasto} onValueChange={(v) => setEditingCrmUser({ ...editingCrmUser, nivel_gasto: v })}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="low" className="text-gray-300">Low Spender</SelectItem>
                            <SelectItem value="medium" className="text-yellow-400">Medium Spender</SelectItem>
                            <SelectItem value="high" className="text-green-400">High Spender</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">💰 Gasto Total ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingCrmUser.gasto_total || 0}
                          onChange={(e) => setEditingCrmUser({ ...editingCrmUser, gasto_total: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Origen / País</Label>
                        <Input
                          value={editingCrmUser.origen || ''}
                          onChange={(e) => setEditingCrmUser({ ...editingCrmUser, origen: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-300">Gustos / Preferencias</Label>
                      <Input
                        value={editingCrmUser.gustos || ''}
                        onChange={(e) => setEditingCrmUser({ ...editingCrmUser, gustos: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Solicitudes Frecuentes</Label>
                      <Input
                        value={editingCrmUser.solicitudes || ''}
                        onChange={(e) => setEditingCrmUser({ ...editingCrmUser, solicitudes: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>

                    {/* ============ CASHBACK SECTION ============ */}
                    <div className="border-t border-zinc-700 pt-4 mt-2 bg-red-500/5 -mx-2 px-4 py-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-red-400 text-lg">⚠️</span>
                        <h4 className="text-white font-semibold">Registro de Cashback (Posible Estafador)</h4>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          id="cashback-toggle"
                          type="checkbox"
                          checked={!!editingCrmUser.hizo_cashback}
                          onChange={(e) => setEditingCrmUser({ ...editingCrmUser, hizo_cashback: e.target.checked })}
                          className="w-5 h-5 rounded accent-red-600"
                          data-testid="cashback-toggle"
                        />
                        <label htmlFor="cashback-toggle" className="text-gray-300 text-sm cursor-pointer">
                          Este usuario realizó un cashback / chargeback
                        </label>
                      </div>
                      {editingCrmUser.hizo_cashback && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-gray-300 text-xs">Fecha del cashback</Label>
                              <Input
                                type="date"
                                value={editingCrmUser.fecha_cashback || ''}
                                onChange={(e) => setEditingCrmUser({ ...editingCrmUser, fecha_cashback: e.target.value })}
                                className="bg-zinc-800 border-zinc-700 text-white"
                                data-testid="cashback-fecha"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300 text-xs">Monto cashback ($)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editingCrmUser.monto_cashback || 0}
                                onChange={(e) => setEditingCrmUser({ ...editingCrmUser, monto_cashback: parseFloat(e.target.value) || 0 })}
                                className="bg-zinc-800 border-zinc-700 text-white"
                                data-testid="cashback-monto"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-gray-300 text-xs">Notas del cashback</Label>
                            <Input
                              value={editingCrmUser.notas_cashback || ''}
                              onChange={(e) => setEditingCrmUser({ ...editingCrmUser, notas_cashback: e.target.value })}
                              placeholder="Razón, plataforma, evidencia, etc."
                              className="bg-zinc-800 border-zinc-700 text-white"
                              data-testid="cashback-notas"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button onClick={handleUpdateCrmUser} className="flex-1 bg-blue-600 hover:bg-blue-700">
                        Guardar Cambios
                      </Button>
                    </div>

                    {/* Chat/Notas Section */}
                    <div className="border-t border-zinc-700 pt-4 mt-2">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        💬 Historial de Interacciones
                        <span className="text-xs text-gray-500">({crmMessages.length} mensajes)</span>
                      </h4>
                      
                      {/* Message Input */}
                      <div className="flex gap-2 mb-4">
                        <Select value={crmMessageType} onValueChange={setCrmMessageType}>
                          <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="note" className="text-gray-300">📝 Nota</SelectItem>
                            <SelectItem value="interaction" className="text-blue-400">💬 Interacción</SelectItem>
                            <SelectItem value="alert" className="text-red-400">⚠️ Alerta</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={newCrmMessage}
                          onChange={(e) => setNewCrmMessage(e.target.value)}
                          placeholder="Escribe una nota o registro de interacción..."
                          className="flex-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                          onKeyPress={(e) => e.key === 'Enter' && handleSendCrmMessage()}
                        />
                        <Button 
                          onClick={handleSendCrmMessage} 
                          className="bg-green-600 hover:bg-green-700"
                          disabled={!newCrmMessage.trim()}
                        >
                          Enviar
                        </Button>
                      </div>

                      {/* Messages List */}
                      <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                        {loadingMessages ? (
                          <div className="text-center py-4">
                            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                          </div>
                        ) : crmMessages.length === 0 ? (
                          <p className="text-gray-500 text-sm text-center py-4">
                            No hay mensajes aún. ¡Agrega el primero!
                          </p>
                        ) : (
                          crmMessages.map((msg) => (
                            <div 
                              key={msg.id} 
                              className={`p-3 rounded-lg ${
                                msg.message_type === 'alert' ? 'bg-red-500/10 border border-red-500/30' :
                                msg.message_type === 'interaction' ? 'bg-blue-500/10 border border-blue-500/30' :
                                'bg-zinc-800 border border-zinc-700'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs">
                                      {msg.message_type === 'alert' ? '⚠️' : msg.message_type === 'interaction' ? '💬' : '📝'}
                                    </span>
                                    <span className="text-sm font-medium text-white">{msg.author_name}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      msg.author_role === 'administrativo' || msg.author_role === 'ceo' 
                                        ? 'bg-purple-500/20 text-purple-400' 
                                        : 'bg-blue-500/20 text-blue-400'
                                    }`}>
                                      {msg.author_role === 'administrativo' ? 'CEO' : msg.author_role}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {new Date(msg.created_at).toLocaleDateString('es-CO', { 
                                        day: '2-digit', 
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-300">{msg.content}</p>
                                </div>
                                <button
                                  onClick={() => handleDeleteCrmMessage(msg.id)}
                                  className="text-gray-500 hover:text-red-400 text-xs p-1"
                                  title="Eliminar mensaje"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 pt-2 border-t border-zinc-700">
                      <p>Creado por: {editingCrmUser.created_by_name} • {new Date(editingCrmUser.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="space-y-6">
            <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Gestión de Usuarios</CardTitle>
                <Dialog open={openCreateDialog} onOpenChange={setOpenCreateDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-green-600 hover:bg-green-700">
                      + Crear Usuario
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-white">Crear Nuevo Usuario 👤</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-300">Email (Usuario)</Label>
                        <Input
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                          placeholder="usuario@ejemplo.com"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Nombre Completo</Label>
                        <Input
                          value={newUser.name}
                          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                          className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                          placeholder="Juan Pérez"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Username (para login)</Label>
                        <Input
                          value={newUser.username || ''}
                          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                          placeholder="usuario123"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Contraseña</Label>
                        <Input
                          type="password"
                          value={newUser.password || ''}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                          placeholder="Contraseña segura"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Rol</Label>
                        <Select
                          value={newUser.role}
                          onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                        >
                          <SelectTrigger className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                            <SelectItem value="chatter">Chatter</SelectItem>
                            <SelectItem value="uploader">Uploader</SelectItem>
                            <SelectItem value="asistente">Asistente</SelectItem>
                            <SelectItem value="administrativo">Administrativo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleCreateUser}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        Crear Usuario
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((usr) => (
                    <div key={usr.id} className="flex justify-between items-center p-4 bg-[#0a0a0a] border border-white/[0.08] shadow-[0_2px_8px_rgba(0,0,0,0.4)] rounded-lg">
                      <div>
                        <div className="font-semibold text-white">{usr.name}</div>
                        <div className="text-sm text-gray-400">{usr.email}</div>
                        <div className="text-xs text-blue-400 mt-1">Rol: {usr.role}</div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditingUser({ ...usr, _originalUsername: usr.username, newPassword: "" });
                            setOpenEditDialog(true);
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`edit-user-btn-${usr.id}`}
                        >
                          ✏️ Editar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleResetPassword(usr.id, usr.name)}
                          className="bg-amber-600 hover:bg-amber-700"
                          data-testid={`reset-password-btn-${usr.id}`}
                          title="Resetear contraseña"
                        >
                          🔑 Reset
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleGenerateMagicLink(usr.id, usr.name)}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid={`magic-link-btn-${usr.id}`}
                          title="Generar link de acceso directo (sin contraseña). Útil cuando el trabajador no puede entrar."
                        >
                          🔗 Link
                        </Button>
                        <Select
                          value={usr.role}
                          onValueChange={(value) => handleUpdateUserRole(usr.id, value)}
                        >
                          <SelectTrigger className="w-32 bg-zinc-700 border-zinc-600 text-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                            <SelectItem value="chatter">Chatter</SelectItem>
                            <SelectItem value="uploader">Uploader</SelectItem>
                            <SelectItem value="asistente">Asistente</SelectItem>
                            <SelectItem value="administrativo">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          onClick={() => handleDeleteUser(usr.id, usr.name)}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid={`delete-user-btn-${usr.id}`}
                        >
                          🗑️
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "newsletter" && (
          <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
            <NewsletterPanel />
          </div>
        )}

        {activeTab === "jgmpro" && (
          <div className="rounded-2xl">
            <JGMProPanel />
          </div>
        )}

        {activeTab === "chatterly" && (
          <div className="rounded-2xl">
            <ChatterlyPanel />
          </div>
        )}

        {activeTab === "email-aliases" && (
          <div className="rounded-2xl">
            <EmailAliasesPanel />
          </div>
        )}

        {activeTab === "calculadora" && (
          <div className="rounded-2xl">
            <CustomCalculator />
          </div>
        )}

        {activeTab === "ventas-mym" && (
          <div className="rounded-2xl">
            <VentasMymPanel />
          </div>
        )}

        {activeTab === "metas" && (
          <div className="rounded-2xl">
            <MetasPanel />
          </div>
        )}

        {activeTab === "solicitudes" && (
          <div className="rounded-2xl">
            <SolicitudesPanel currentUserRole={user?.role} />
          </div>
        )}


        {/* Edit User Dialog */}
        <Dialog open={openEditDialog} onOpenChange={setOpenEditDialog}>
          <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Editar Usuario</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Nombre Completo</Label>
                  <Input
                    value={editingUser.name || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    data-testid="edit-user-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Username (para iniciar sesión)</Label>
                  <Input
                    value={editingUser.username || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                    placeholder="Username actual"
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    data-testid="edit-user-username-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Nueva Contraseña (opcional)</Label>
                  <Input
                    type="text"
                    value={editingUser.newPassword || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, newPassword: e.target.value })}
                    placeholder="Dejar vacío si no quieres cambiarla"
                    className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                    data-testid="edit-user-password-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Mínimo 6 caracteres. Al cambiarla, el usuario debe iniciar sesión de nuevo.</p>
                </div>
                <Button
                  onClick={handleUpdateUserName}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="save-user-changes-btn"
                >
                  Guardar Cambios
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Goals History Dialog */}
        <Dialog open={openHistoryDialog} onOpenChange={setOpenHistoryDialog}>
          <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Historial de Metas Completadas</DialogTitle>
            </DialogHeader>
            
            {/* Date Filters */}
            <div className="flex gap-3 mb-4 p-4 bg-zinc-800/50 rounded-lg">
              <div className="flex-1">
                <Label className="text-gray-300 text-xs mb-1 block">Fecha Inicio</Label>
                <Input
                  type="date"
                  value={historyDateFilter.start}
                  onChange={(e) => setHistoryDateFilter({ ...historyDateFilter, start: e.target.value })}
                  className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                />
              </div>
              <div className="flex-1">
                <Label className="text-gray-300 text-xs mb-1 block">Fecha Fin</Label>
                <Input
                  type="date"
                  value={historyDateFilter.end}
                  onChange={(e) => setHistoryDateFilter({ ...historyDateFilter, end: e.target.value })}
                  className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl text-white"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={() => setHistoryDateFilter({ start: '', end: '' })}
                  variant="outline"
                  className="bg-zinc-700 hover:bg-zinc-600 text-white border-zinc-600"
                >
                  Limpiar
                </Button>
              </div>
            </div>

            {/* Results Summary */}
            {(historyDateFilter.start || historyDateFilter.end) && (
              <div className="text-sm text-gray-400 mb-2 px-4">
                Mostrando {filteredGoalsHistory.length} de {goalsHistory.length} metas
              </div>
            )}

            <div className="space-y-4">
              {filteredGoalsHistory.length === 0 ? (
                <p className="text-gray-400 text-center py-8">
                  {goalsHistory.length === 0 ? 'No hay metas completadas' : 'No hay metas en el rango de fechas seleccionado'}
                </p>
              ) : (
                <div className="space-y-3">
                  {filteredGoalsHistory.map((goal) => (
                    <Card 
                      key={goal.id} 
                      className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl hover:border-purple-500 cursor-pointer transition-all"
                      onClick={() => handleViewGoalReports(goal)}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="text-white font-bold">{goal.user_name}</h3>
                            <p className="text-sm text-gray-400">{goal.period_name}</p>
                            <p className="text-xs text-gray-500">
                              {goal.fecha_inicio} - {goal.fecha_fin}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-500">${goal.goal_amount}</p>
                            <p className="text-xs text-gray-400">
                              Completada: {new Date(goal.completed_at).toLocaleDateString('es')}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Goal Reports Dialog */}
        <Dialog open={openReportsDialog} onOpenChange={setOpenReportsDialog}>
          <DialogContent className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl max-w-5xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">
                {selectedGoalDetails && `Reportes - ${selectedGoalDetails.user_name}`}
              </DialogTitle>
              {selectedGoalDetails && (
                <p className="text-sm text-gray-400">
                  {selectedGoalDetails.period_name} | Meta: ${selectedGoalDetails.goal_amount}
                </p>
              )}
            </DialogHeader>
            <div className="space-y-4">
              {selectedGoalReports.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No hay reportes en este período</p>
              ) : (
                <>
                  {/* Summary */}
                  <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-gray-400">Total Ganado</p>
                          <p className="text-2xl font-bold text-green-500">
                            ${selectedGoalReports.reduce((sum, r) => sum + (r.money_collected || 0), 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Horas Trabajadas</p>
                          <p className="text-2xl font-bold text-blue-500">
                            {selectedGoalReports.reduce((sum, r) => sum + (r.hours_worked || 0), 0).toFixed(1)}h
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Reportes</p>
                          <p className="text-2xl font-bold text-purple-500">
                            {selectedGoalReports.length}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Reports Table */}
                  <div className="space-y-2">
                    {selectedGoalReports.map((report) => (
                      <Card key={report.id} className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-xl">
                        <CardContent className="p-3">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-white font-medium">
                                {new Date(report.date + 'T12:00:00').toLocaleDateString('es', { 
                                  weekday: 'long', 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}
                              </p>
                              <p className="text-sm text-gray-400">
                                {report.hours_worked}h trabajadas
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-green-500">${report.money_collected}</p>
                              {report.subs_amount > 0 && (
                                <p className="text-xs text-gray-400">Subs: ${report.subs_amount}</p>
                              )}
                              {report.messages_amount > 0 && (
                                <p className="text-xs text-gray-400">Msgs: ${report.messages_amount}</p>
                              )}
                              {report.tips_amount > 0 && (
                                <p className="text-xs text-gray-400">Tips: ${report.tips_amount}</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

      {/* Calendario de Ventas Modal */}
      <CalendarioVentas
        isOpen={openCalendarioVentas}
        onClose={() => {
          setOpenCalendarioVentas(false);
          setEmpleadoSeleccionadoVentas(null);
        }}
        empleadoId={empleadoSeleccionadoVentas?.id}
        empleadoNombre={empleadoSeleccionadoVentas?.nombre || 'Todos'}
        mostrarTodos={!empleadoSeleccionadoVentas}
      />

      {/* Profile Edit Dialog */}
      <Dialog open={openProfileDialog} onOpenChange={setOpenProfileDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Settings className="w-5 h-5" /> Editar Perfil
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Avatar/Photo Section */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                {profileData.profile_picture ? (
                  <img 
                    src={profileData.profile_picture} 
                    alt="Foto de perfil" 
                    className="w-24 h-24 rounded-full object-cover border-4 border-purple-500"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white font-bold text-3xl">{profileData.name?.charAt(0) || 'A'}</span>
                  </div>
                )}
                <label 
                  htmlFor="profile-picture-input"
                  className="absolute bottom-0 right-0 bg-purple-600 hover:bg-purple-700 rounded-full p-2 cursor-pointer transition-colors"
                >
                  <Sparkles className="w-4 h-4 text-white" />
                </label>
                <input
                  id="profile-picture-input"
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                  className="hidden"
                />
              </div>
              {uploadingPicture && (
                <p className="text-sm text-gray-400">Cargando imagen...</p>
              )}
              <p className="text-xs text-gray-500">Haz clic en el ícono para cambiar la foto</p>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="profile-name" className="text-gray-300">Nombre Completo</Label>
              <Input
                id="profile-name"
                value={profileData.name}
                onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Tu nombre"
                className="bg-zinc-800 border-zinc-600 text-white"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="profile-phone" className="text-gray-300">Teléfono (opcional)</Label>
              <Input
                id="profile-phone"
                value={profileData.phone}
                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+57 300 123 4567"
                className="bg-zinc-800 border-zinc-600 text-white"
              />
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label className="text-gray-300">Email</Label>
              <Input
                value={user?.email || ''}
                disabled
                className="bg-zinc-700 border-zinc-600 text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500">El email no puede ser modificado</p>
            </div>

            {/* Role (read-only) */}
            <div className="space-y-2">
              <Label className="text-gray-300">Rol</Label>
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-yellow-500" />
                <span className="text-gray-300 capitalize">{user?.role || 'Usuario'}</span>
              </div>
            </div>

            {/* Save Button */}
            <Button 
              onClick={handleSaveProfile}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              Guardar Cambios
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Chat Modal */}
      <TeamChat 
        isOpen={teamChatOpen} 
        onClose={() => {
          setTeamChatOpen(false);
          fetchChatUnreadCount();
        }}
        currentUser={user}
      />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;